import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ChartComp from "../components/chart/Chart";
import { toast } from "react-toastify";
import PageContainer from "../components/pages/PageContainer";
import { getCandlestickData } from "../utils/api";
import { polygonCluster, polygonWsSymbol } from "../utils/polygonSymbol";
import {
  getAdlDataNew1,
  getCCIDataNew1,
  getCMFDataNew1,
  getMACDDataNew1,
  getMACDHDataNew1,
  getEFIDataNew1,
  getMFIDataNew1,
  getOBVDataNew1,
  getROCDataNew1,
  getSTOCHDataNew1,
  getRSIDataNew1,
  getADXDataNew1,
  getPPODataNew1,
  getPVODataNew1,
  getSMADataNew1,
  getBollingerBandsDataNew1,
  getEMADataNew1,
  getPsarDataNew1,
} from "../utils/api";
import { getBollingerBandsDataNew, getSMADataNew, getEMADataNew, getPsarDataNew } from "../api/user/index";
import { transformApiDataToChartFormat, mergeChartData, formatTimestampForApi, indicatorRowBudget } from "../utils/chartDataHelpers";
import {
  addChartNotification,
  deleteSavedChart,
  getChartStockDetails,
  getFollowersListApi,
  getSavedCharts,
  s3SingleUpload,
  saveChart,
  shareCartAsPostApi,
  shareSavedChartApi,
  getUserWatchlists,
  addStockToWatchlists,
  getGuideSetting,
  searchStocksApi,
  createPost
} from "../api/user";
import { initialValue, saveStateToLocalStorage } from "../utils/localstorage";
import "react-toastify/dist/ReactToastify.css";
import Annotation from "../components/setting/Annotation";
import ChartSettingsControl from "../components/setting/ChartSettingsControl";
import ChartType from "../components/setting/ChartType";
import Overlays from "../components/setting/Overlays";
import Indicators from "../components/setting/Indicators";
import html2canvas from "html2canvas";
import DashboardHeader from "../components/Other/DashboardHeader";
import PageHeaderTitle from "../components/pages/PageHeaderTitle";
import { useOutletContext } from "react-router";
import TradeSkeleton, { ChartAreaSkeleton } from "../components/Skeleton/TradeSkeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { StockSummary } from "../components/chart/stockSummary";
import { ChartToolBar } from "../components/chart/chartToolBar";
import { TimeRangeButton } from "../components/chart/timeRangeButton";
import { SavedCharts } from "../components/chart/savedCharts";
import { SaveChartImage } from "../components/chart/saveChartImage";
import { ChartError } from "../components/chart/chartError";
import { ChartActionButtons } from "../components/chart/chartActionButtons";
import { ChartTopSummary } from "../components/chart/chartTopSummary";
import { AddNotification } from "../components/chart/addNotification";
import MobileChartToolbar from "../components/chart/MobileChartToolbar";
import DesktopChartLayout from "../components/chart/desktop/DesktopChartLayout";
import { isPlausibleSymbol, useLeagueTrade } from "../context/LeagueTradeContext";
import {
  getSidebarCollapseOverride,
  isFullScreenChartRoute,
  subscribeSidebarCollapse,
} from "../utils/sidebarCollapse";
import FollowersListModal from "../components/Modals/followersListModal";
import { fetchChartsData } from "./fetchChartsData";
import { sleep, isQuillEmpty } from "../utils/utils";
import PostRichTextEditor from "../components/pages/PostRichTextEditor";
import $ from "jquery";
import ChartGuideSteps from "../components/guide/ChartGuideSteps";
import MobileChartGuideSteps from "../components/guide/MobileChartGuideSteps";
import NewTraderGuide from "../components/guide/NewTraderGuide";
import { useNewTraderGuide } from "../context/NewTraderGuideContext";
import useMobileMenuOpen from "../hooks/useMobileMenuOpen";

// Custom hook for live chart data
const useLiveChartData = (tickerFromLocalStorage, setting, initialData) => {
  const [liveData, setLiveData] = useState(initialData || []);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [previousPrice, setPreviousPrice] = useState(null);
  const wsRef = useRef(null);
  const lastUpdateTimeRef = useRef(null);
  const intervalRef = useRef(null);
  // Wall-clock timestamp at which the current intraday bar should roll to the
  // next one. Anchored to a real interval boundary when live data loads, so the
  // active bar keeps moving for a full interval instead of a new bar spawning
  // mid-window. null = not yet anchored (re-anchors after each (re)load).
  const currentBarEndRef = useRef(null);
  const apiKey = process.env.REACT_APP_API_KEY;

  // Function to get time interval in milliseconds based on period and multiplier
  const getTimeInterval = (period, multiplier = 1) => {
    // Base intervals for each period type
    const baseIntervals = {
      'minute': 60000,      // 1 minute base
      'hour': 3600000,      // 1 hour base
      'day': 86400000,      // 1 day
      'week': 604800000,    // 1 week
      'month': 2592000000,  // 1 month (approx)
      // Also support shorthand formats
      '1m': 60000,
      '2m': 120000,
      '5m': 300000,
      '15m': 900000,
      '30m': 1800000,
      '1h': 3600000,
      '4h': 14400000,
      '1D': 86400000,
      '1W': 604800000,
      '1M': 2592000000
    };

    // If using base period types (minute, hour, etc.), multiply by the multiplier
    if (['minute', 'hour'].includes(period)) {
      return (baseIntervals[period] || 60000) * multiplier;
    }

    // For day, week, month - just return base (multiplier usually 1)
    if (['day', 'week', 'month'].includes(period)) {
      return baseIntervals[period] || 86400000;
    }

    // For shorthand formats (1m, 5m, 1h, etc.) - return directly
    return baseIntervals[period] || 60000; // Default to 1 minute
  };

  // Function to round timestamp to nearest interval
  const roundToInterval = (timestamp, interval) => {
    return Math.floor(timestamp / interval) * interval;
  };

  // Function to update live data
  const updateLiveData = useCallback((newPrice, volume = 0, options = {}) => 
    {
    const { isSimulated = false } = options;
    if (!liveData.length) {
      console.log("No live data available to update");
      return;
    }

    // Update price tracking for display
    setPreviousPrice(currentPrice);
    setCurrentPrice(newPrice);

    const period = setting?.period || '1m';

    // For hourly, daily, weekly, monthly timeframes - ONLY update the current bar's close price
    // Do NOT create new bars from websocket data for these timeframes
    // New bars for 1h/4h/D/W/M should only come from API data refresh
    const isLongTimeframe = ['1h', '4h', '1D', '1W', '1M', 'hour', 'day', 'week', 'month'].includes(period);

    if (isLongTimeframe) {
      // For daily/weekly/monthly: only update the last bar's close price
      setLiveData(prevData => {
        if (!prevData.length) return prevData;
        const newData = [...prevData];
        const currentBar = { ...newData[newData.length - 1] };

        // Update close price
        currentBar.close = newPrice;

        // Update high/low only if within reasonable bounds (avoid artificial wicks)
        const openPrice = currentBar.open ?? currentBar.close;
        const deltaFromOpen = Math.abs(newPrice - openPrice) / (openPrice || 1);
        const maxDelta = 0.02; // 2% for daily bars is reasonable intraday movement

        if (!isSimulated && newPrice > currentBar.high && deltaFromOpen < maxDelta) {
          currentBar.high = newPrice;
        }
        if (!isSimulated && newPrice < currentBar.low && deltaFromOpen < maxDelta) {
          currentBar.low = newPrice;
        }

        currentBar.volume += volume;
        newData[newData.length - 1] = currentBar;
        console.log(`[${period}] Updated current bar (no new bars for D/W/M):`, currentBar);
        return newData;
      });

      lastUpdateTimeRef.current = Date.now();
      return; // Exit early - don't create new bars for long timeframes
    }

    // For intraday timeframes (1m/2m/5m/15m/30m): keep the CURRENT bar moving and
    // roll to a new bar only after a full interval of real (wall-clock) time has
    // elapsed, measured from an anchor set when live data loads.
    //
    // Previously a new bar was appended whenever the client-clock bucket differed
    // from the LAST HISTORICAL bar's bucket. The API's latest intraday bar usually
    // isn't already in the current wall-clock bucket (feed latency / timestamp
    // grid), so the first tick tripped that check and spawned a premature bar
    // mid-window. Anchoring to elapsed time makes intraday behave like 1h+.
    const currentTime = Date.now();
    const multiplier = setting?.multiplier || 1;
    const interval = getTimeInterval(period, multiplier);

    if (currentBarEndRef.current == null) {
      // First tick after a (re)load: the last bar IS the current bar; it should
      // run until the next real interval boundary.
      currentBarEndRef.current = roundToInterval(currentTime, interval) + interval;
    }
    // Decide synchronously (before the async state update) whether this tick rolls
    // the bar, advancing the anchor past any fully-elapsed intervals so even a long
    // gap yields exactly ONE new bar.
    let rolled = false;
    if (currentTime >= currentBarEndRef.current) {
      rolled = true;
      while (currentTime >= currentBarEndRef.current) currentBarEndRef.current += interval;
    }

    setLiveData(prevData => {
      if (!prevData.length) return prevData;
      const newData = [...prevData];
      const lastDataPoint = newData[newData.length - 1];

      if (!rolled) {
        // Update current bar safely to avoid artificial long wicks
        const currentBar = { ...newData[newData.length - 1] };

        // Always update close
        currentBar.close = newPrice;

        // Only update high/low if change is within reasonable bounds
        const openPrice = currentBar.open ?? currentBar.close;
        const deltaFromOpen = Math.abs(newPrice - openPrice) / (openPrice || 1);
        const maxDelta = 0.005; // 0.5% per tick allowed

        if (!isSimulated && newPrice > currentBar.high && deltaFromOpen < maxDelta) {
          currentBar.high = newPrice;
        }
        if (!isSimulated && newPrice < currentBar.low && deltaFromOpen < maxDelta) {
          currentBar.low = newPrice;
        }

        currentBar.volume += volume;
        newData[newData.length - 1] = currentBar;
        console.log("Updated current bar:", currentBar);
      } else {
        // Create new bar with proper OHLC initialization. Its date is contiguous
        // with the last bar's own timestamp grid (lastBar + interval) so the axis
        // stays consistent with the historical candles.
        const lastClose = lastDataPoint.close;
        const newBar = {
          key: Date.now(),
          date: new Date(new Date(lastDataPoint.date).getTime() + interval),
          open: lastClose,
          high: lastClose, // Initialize high to last close
          low: lastClose,  // Initialize low to last close
          close: lastClose, // Will be updated immediately with new price
          volume: volume,
          chart: [],
          indicators: []
        };

        // Immediately update close; constrain initial high/low movement
        newBar.close = newPrice;
        const initialDelta = Math.abs(newPrice - lastClose) / (lastClose || 1);
        const maxInitialDelta = isSimulated ? 0 : 0.001; // 0% if simulated; 0.1% if real
        if (!isSimulated && initialDelta < maxInitialDelta) {
          newBar.high = Math.max(lastClose, newPrice);
          newBar.low = Math.min(lastClose, newPrice);
        } // else keep high/low at lastClose to avoid long tails

        newData.push(newBar);
        console.log("Created new bar:", newBar);

        // Keep only last 200 data points to prevent memory issues
        if (newData.length > 200) {
          newData.shift();
        }
      }

      return newData;
    });

    lastUpdateTimeRef.current = currentTime;
  }, [liveData, setting?.period, setting?.multiplier, currentPrice]);

  // Function to check if we need to create a new bar based on time
  const checkAndCreateNewBar = useCallback(() => {
    if (!liveData.length) return;

    const period = setting?.period || '1m';

    // Skip automatic bar creation for hourly, daily, weekly, monthly timeframes
    // These timeframes should only get new bars from API data refresh
    const isLongTimeframe = ['1h', '4h', '1D', '1W', '1M', 'hour', 'day', 'week', 'month'].includes(period);
    if (isLongTimeframe) {
      console.log(`[${period}] Skipping automatic bar creation for long timeframe`);
      return;
    }

    const currentTime = Date.now();
    const multiplier = setting?.multiplier || 1;
    const interval = getTimeInterval(period, multiplier);

    // Same wall-clock anchor as updateLiveData (shared ref) — only roll after a
    // full interval has actually elapsed, never mid-window.
    if (currentBarEndRef.current == null) {
      currentBarEndRef.current = roundToInterval(currentTime, interval) + interval;
    }

    if (currentTime >= currentBarEndRef.current) {
      while (currentTime >= currentBarEndRef.current) currentBarEndRef.current += interval;
      // Time to create a new bar (contiguous with the last bar's timestamp grid)
      const lastDataPoint = liveData[liveData.length - 1];
      const lastClose = lastDataPoint.close;
      const newBar = {
        key: Date.now(),
        date: new Date(new Date(lastDataPoint.date).getTime() + interval),
        open: lastClose,
        high: lastClose,
        low: lastClose,
        close: lastClose,
        volume: 0,
        chart: [],
        indicators: []
      };

      setLiveData(prevData => {
        const newData = [...prevData, newBar];
        // Keep only last 200 data points
        if (newData.length > 200) {
          newData.shift();
        }
        console.log("Time-based new bar created:", newBar);

        // Minute-end reconciliation: ensure the just-closed bar exactly matches API OHLC
        // Run a short delay to allow backend aggregation to finalize
        try {
          const justClosedBarIndex = newData.length - 2; // bar before the newly created one
          if (justClosedBarIndex >= 0) {
            const justClosedBar = newData[justClosedBarIndex];
            const periodForApi = (setting?.period || '1m');
            const apiPeriod = {
              '1m': 'minute', '2m': 'minute', '5m': 'minute', '15m': 'minute', '30m': 'minute',
              '1h': 'hour', '4h': 'hour', '1D': 'day', '1W': 'week', '1M': 'month'
            }[periodForApi] || 'minute';

            // Fetch a small window and reconcile the matching timestamp
            setTimeout(async () => {
              try {
                const symbolForApi = tickerFromLocalStorage;
                const result = await getCandlestickData(symbolForApi, apiPeriod);
                if (Array.isArray(result) && result.length) {
                  // Find the API bar that matches our just-closed bar minute
                  const closedTs = new Date(justClosedBar.date).getTime();
                  const matched = result.find((b) => {
                    const bt = new Date(b.date || b.t || b.timestamp || b.Time || b.time).getTime();
                    // Compare by rounded minute boundary
                    return Math.floor(bt / 60000) === Math.floor(closedTs / 60000);
                  });

                  if (matched && typeof matched === 'object') {
                    const apiOpen = matched.open ?? matched.o;
                    const apiHigh = matched.high ?? matched.h;
                    const apiLow = matched.low ?? matched.l;
                    const apiClose = matched.close ?? matched.c;
                    const apiVolume = matched.volume ?? matched.v ?? justClosedBar.volume;

                    if ([apiOpen, apiHigh, apiLow, apiClose].every((v) => typeof v === 'number' && isFinite(v))) {
                      setLiveData((dataState) => {
                        const copy = [...dataState];
                        if (copy[justClosedBarIndex]) {
                          copy[justClosedBarIndex] = {
                            ...copy[justClosedBarIndex],
                            open: apiOpen,
                            high: apiHigh,
                            low: apiLow,
                            close: apiClose,
                            volume: apiVolume,
                          };
                          console.log('Reconciled just-closed bar with API OHLC:', copy[justClosedBarIndex]);
                        }
                        return copy;
                      });
                    }
                  }
                }
              } catch (e) {
                console.warn('Minute-end reconciliation failed:', e);
              }
            }, 2000);
          }
        } catch (e) {
          console.warn('Reconciliation scheduling error:', e);
        }
        return newData;
      });
    }
  }, [liveData, setting?.period, setting?.multiplier]);

  // WebSocket connection and data handling
  useEffect(() => {
    if (!apiKey || !tickerFromLocalStorage) return;

    const wsPath = polygonCluster(tickerFromLocalStorage);

    // The channel name, not the REST ticker — see utils/polygonSymbol. This used
    // to hyphenate crypto only, so every FOREX subscription was addressed to a
    // channel Polygon accepts and never sends anything on.
    const formattedSymbol = polygonWsSymbol(tickerFromLocalStorage);

    // Close existing connection only if ticker changed
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Check if it's the same ticker - if so, don't reconnect
      if (wsRef.current.ticker === tickerFromLocalStorage) {
        console.log("📊LiveChart: Already connected to", tickerFromLocalStorage);
        return;
      }
      // Different ticker - close and reconnect
      wsRef.current.close();
      console.log("📊 LiveChart: Closing connection for ticker change");
    }

    const ws = new WebSocket(`wss://business.polygon.io/${wsPath}?apiKey=${apiKey}`);
    ws.ticker = tickerFromLocalStorage; // Store ticker on WebSocket object
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("📊 LiveChart: ✅ Connected successfully");
      ws.send(JSON.stringify({ action: "auth", params: apiKey }));

      console.log(`📊 LiveChart: 🔄 Symbol conversion: ${tickerFromLocalStorage} → ${formattedSymbol}`);

      const fmvSubscription = `FMV.${formattedSymbol}`;
      ws.send(JSON.stringify({ action: "subscribe", params: fmvSubscription }));
      console.log(`📊 LiveChart: 📡 Subscribed to FMV: ${fmvSubscription}`);
    };

    ws.onmessage = (event) => {
      try {
        const response = JSON.parse(event.data);

        if (response !== null && response !== undefined && Array.isArray(response) && response.length > 0) {
          // Same rule as the subscription — msg.sym comes back in channel form
          // (Polygon answers FMV.C:EUR-USD with sym "C:EUR-USD").
          const formattedSymbol = polygonWsSymbol(tickerFromLocalStorage);

          response.forEach((msg, index) => {
            // Only use FMV (Fair Market Value) for consistency with ChartTopSummary
            if (msg.ev === "FMV" && msg.sym === formattedSymbol) {
              const price = msg.fmv;
              console.log("📊 LiveChart: 🔥 FMV price received:", price, "for", msg.sym);

              if (price && typeof price === 'number' && price > 0) {
                updateLiveData(price, msg.v || msg.s || 0);

                // Calculate bid/ask with small spread
                const spreadPercent = Math.random() * (0.0005 - 0.0001) + 0.0001;
                const bid = price * (1 - spreadPercent);
                const ask = price * (1 + spreadPercent);

                // Dispatch RAW bid/ask — chartTopSummary formats to the asset
                // class's precision itself. Rounding to 2dp here destroyed the
                // digits a sub-$1 crypto or forex pair needs.

                // Broadcast price to ChartTopSummary to avoid connection limits
                window.dispatchEvent(new CustomEvent('livePriceUpdate', {
                  detail: { price }
                }));

                // Dispatch quotes update for SELL/BUY boxes
                window.dispatchEvent(new CustomEvent('quotesUpdate', {
                  detail: { bid, ask }
                }));
              }
            } else if (msg.ev === "status") {
              // Only log important status messages
              if (msg.status === "max_connections" || msg.status === "auth_success") {
                console.log(`📊 LiveChart: 📋 Status: ${msg.status}`);
              }
            }
          });
        }
      } catch (error) {
        console.error("📊 LiveChart: ❌ Message parsing error:", error);
      }
    };

    ws.onerror = (error) => {
      console.error("📊 LiveChart: ❌ WebSocket error:", error);
    };

    ws.onclose = () => {
      console.log("📊 LiveChart: 🔴 Connection closed");
    };

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        console.log("Live chart WebSocket cleaned up");
      }
    };
  }, [apiKey, tickerFromLocalStorage, updateLiveData]); // Removed setting.period dependency

  // Set up interval to check for new bars every 10 seconds
  useEffect(() => {
    const interval = getTimeInterval(setting?.period || '1m', setting?.multiplier || 1);
    const checkInterval = Math.min(interval / 6, 10000); // Check every 10 seconds or 1/6 of interval, whichever is smaller

    intervalRef.current = setInterval(() => {
      checkAndCreateNewBar();

      // NOTHING ELSE. There used to be a "for testing/demo purposes" block here
      // that, if no real price had arrived for 30 seconds, invented one:
      //
      //     const randomChange = (Math.random() - 0.5) * 0.002;   // ±0.2%
      //     updateLiveData(lastPrice * (1 + randomChange), ..., { isSimulated: true });
      //
      // and wrote it straight into the live candle. It shipped.
      //
      // Client, Mantis "Forex data": "you let the chart sit for 30 seconds to a
      // minute, the bar is going to jump either up green or down red. But the
      // price isn't moving… if you click the Trade link, the page refreshes, and
      // you will see the correct bar."
      //
      // Every clause of that is this block. The threshold was literally 30000ms.
      // (Math.random() - 0.5) is why it went up as often as down. The header
      // price never moved because the fabricated tick was fed to updateLiveData
      // directly and never dispatched the `livePriceUpdate` event the price
      // readouts listen to. And a refresh looked correct because the API's real
      // OHLC replaced the invented close.
      //
      // Measured on C:EURUSD before removal: 0 real ticks in 75 seconds and 3
      // fabricated jumps, at +10s, +40s and +70s. On X:BTCUSD over 45 seconds:
      // 21 real ticks and 0 fabricated ones — which is the whole of why the
      // client saw this on forex and not on crypto or stocks. The trigger is
      // "no real data", and forex was the one class receiving none (see
      // utils/polygonSymbol).
      //
      // The forex feed is fixed separately. This stays gone regardless: when
      // there is no live price the candle must simply hold its last real value.
      // Filling the silence with random numbers on a trading chart is never the
      // answer, and it is what turned a quiet feed into visible, wrong data.
    }, checkInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // `liveData` and `updateLiveData` were dependencies only because the removed
    // simulator read them. With it gone the interval would otherwise be torn
    // down and rebuilt on every single price tick, for nothing.
  }, [setting?.period, checkAndCreateNewBar]);

  // Update live data when initial data changes
  useEffect(() => {
    console.log('[useLiveChartData] initialData changed:', { length: initialData?.length, ticker: tickerFromLocalStorage });
    if (initialData && initialData.length > 0) {
      console.log("Setting initial live data:", initialData.length, "points");
      setLiveData(initialData);
      // Re-anchor the intraday roll boundary to the fresh data (next tick sets it
      // to the next real interval boundary from "now").
      currentBarEndRef.current = null;
      lastUpdateTimeRef.current = new Date(initialData[initialData.length - 1]?.date).getTime();

      // Set initial price from the last data point
      const lastPrice = initialData[initialData.length - 1]?.close;
      if (lastPrice) {
        setCurrentPrice(lastPrice);
        setPreviousPrice(lastPrice); // Initially set previous to same as current
      }
    }
  }, [initialData]);

  // Clear prior chart data immediately when ticker OR period changes so we don't display
  // the previous symbol's/period's candles (which caused giant "crazy bars" or white screen during transitions)
  useEffect(() => {
    console.log('[useLiveChartData] Clearing data - ticker or period changed:', { ticker: tickerFromLocalStorage, period: setting?.period });
    setLiveData([]);
    setCurrentPrice(null);
    setPreviousPrice(null);
    lastUpdateTimeRef.current = null;
    currentBarEndRef.current = null;
  }, [tickerFromLocalStorage, setting?.period]);

  return {
    liveData,
    currentPrice,
    previousPrice
  };
};

// The stored ticker is shared by every chart surface, so it can be left holding
// something that isn't a symbol at all — a league slug, historically. Loading
// that renders a blank chart with no way back, so anything implausible is
// discarded and repaired in place.
const storedTickerOrDefault = () => {
  const stored = localStorage.getItem("ticker");
  if (isPlausibleSymbol(stored)) return stored;
  if (stored) localStorage.setItem("ticker", "SPY");
  return "SPY";
};

const Char = () => {
  // Null on the main Trade screen; set when this screen is running inside a
  // league (see LeagueTradePage). Every order placed from here has to stay in
  // that league — see onBuySell below.
  const league = useLeagueTrade();
  const [data, setData] = useState([]);
  const [gridSetting, setGridSetting] = useState({
    style: "ShortDot",
    opacity: "0.1",
    strokeWidth: "2",
    grid: false,
  });

  const [guideIcon, setGuideIcon] = useState(false);
  const mobileMenuOpen = useMobileMenuOpen();
  const [setting, setSetting] = useState(initialValue);
  const captureRef = React.useRef();
  const [stockData, setstockData] = useState(null);
  const [isParamsSubmit, setIsParamSubmit] = useState(false);
  const [isIndicatorSubmit, setIsIndicatorSubmit] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isOverlaySubmit, setIsOverlaySubmit] = useState(false);
  const [overlays, setOverlays] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [indicatorConfig, setIndicatorConfig] = useState([]);
  const [candleStick, setCandleStick] = useState([]);
  const [loader, setloader] = useState(false);
  const [overlayloader, setOverlayloader] = useState(false);
  const [indicatorloader, setIndicatorloader] = useState(false);
  const drawingCanva = useRef();
  const [error, seterror] = useState("");
  const [activeTab, setActiveTab] = useState(1);
  const [price, setPrice] = useState("");
  const { symbol } = useParams();
  const navigate = useNavigate();
  // Use state for tickerFromLocalStorage so it triggers re-renders when updated
  const [tickerFromLocalStorage, setTickerFromLocalStorage] = useState(() =>
    symbol ? symbol.toUpperCase() : storedTickerOrDefault()
  );
  // Ref to track current ticker and prevent duplicate API calls
  const currentTickerRef = useRef(symbol ? symbol.toUpperCase() : storedTickerOrDefault());
  const [loaders, setloaders] = useState(true);
  const [hideZoomControls, setHideZoomControls] = useState(false);
  const [savedCharts, setSavedCharts] = useState([]);
  const [preview, setPreview] = useState(false);
  const [preview1, setPreview1] = useState(false);
  const [activeImage, setActiveImage] = useState("");
  const [activeImage1, setActiveImage1] = useState(
    localStorage.getItem("tickerEventImage") || ""
  );
  const [isSlide, setIsSlide] = useState(false);
  const [editOrder, setEditOrder] = useState(() => {
    try {
      const raw = localStorage.getItem("editOrder");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const clearEditOrder = useCallback(() => {
    localStorage.removeItem("editOrder");
    setEditOrder(null);
  }, []);
  // Drop the edit-order context when leaving /charts so the buy/sell modal
  // doesn't auto-reopen with stale data on a future visit. Re-entering the
  // edit flow always starts from the portfolio's Edit Order button.
  useEffect(() => {
    return () => {
      localStorage.removeItem("editOrder");
    };
  }, []);
  // Exit edit mode automatically when the user picks a different symbol
  // (via search, wheel picker, watchlist, etc.) — the order being edited belongs
  // to its original symbol, so a symbol change means they're done editing it.
  useEffect(() => {
    if (!editOrder?.stock_symbol) return;
    const editSym = String(editOrder.stock_symbol).toUpperCase();
    const curSym = String(tickerFromLocalStorage || "").toUpperCase();
    if (curSym && curSym !== editSym) {
      clearEditOrder();
    }
  }, [tickerFromLocalStorage, editOrder, clearEditOrder]);
  const [sidebarToggle] = useOutletContext();
  const [sidebarCollapsedState, setSidebarCollapsedState] = useState(() => {
    // Includes /<slug>/trade — see isFullScreenChartRoute.
    if (!isFullScreenChartRoute(window.location.pathname)) return false;
    const override = getSidebarCollapseOverride();
    return override === null ? false : override;
  });
  useEffect(() => {
    const read = () => {
      if (!isFullScreenChartRoute(window.location.pathname)) {
        setSidebarCollapsedState(false);
        return;
      }
      const override = getSidebarCollapseOverride();
      setSidebarCollapsedState(override === null ? false : override);
    };
    read();
    return subscribeSidebarCollapse(read);
  }, []);
  const [stockPrice, setStockPrice] = useState("");
  const [quotes, setQuotes] = useState(null);
  const [followersList, setFollowersList] = useState([]);
  const [isFollowersOpen, setIsFollowersOpen] = useState(false);
  const [shareChartData, setShareChartData] = useState({});
  const [shareChartData1, setShareChartData1] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const initialState = {
    selectValue: "",
    inputValue: "",
    selectPosValue: "above",
  };
  const [rows, setRows] = useState([initialState]);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [chartId, setChartId] = useState(null); // State to store chart id

  const [watchlists, setWatchlists] = useState([]);
  const [showModal1, setShowModal1] = useState(false);
  const [selected1, setSelected1] = useState({});
  const [loading1, setLoading1] = useState(false);

  // Watchlist modal swipe-down ref
  const watchlistModalRef = useRef(null);

  // Swipe down to close handler for watchlist modal
  const createWatchlistSwipeHandlers = () => {
    let startY = 0;
    let currentY = 0;

    return {
      onTouchStart: (e) => {
        startY = e.touches[0].clientY;
        currentY = startY;
      },
      onTouchMove: (e) => {
        currentY = e.touches[0].clientY;
        const deltaY = currentY - startY;

        if (deltaY > 0 && watchlistModalRef.current) {
          watchlistModalRef.current.style.transform = `translateY(${deltaY}px)`;
          watchlistModalRef.current.style.transition = 'none';
        }
      },
      onTouchEnd: () => {
        const deltaY = currentY - startY;

        if (deltaY > 80) {
          setShowModal1(false);
        }

        if (watchlistModalRef.current) {
          watchlistModalRef.current.style.transform = '';
          watchlistModalRef.current.style.transition = 'transform 0.3s ease-out';
        }
      }
    };
  };

  const [dropdownStates1, setDropdownStates1] = useState(
    savedCharts.map(() => false)
  );

  // Chart Guide State - can be triggered manually or with Ctrl+G
  const [showChartGuide, setShowChartGuide] = useState(false);
  const [showMobileChartGuide, setShowMobileChartGuide] = useState(false);
  const { guideActive: newTraderGuideActive, guideStep: newTraderGuideStep } = useNewTraderGuide();

  // Force chart to daily timeframe when the "this is the live daily chart" guide step is shown
  useEffect(() => {
    if (newTraderGuideActive && newTraderGuideStep === 2) {
      setSetting((prev) => ({
        ...prev,
        period: "day",
        multiplier: 1,
        periodID: 8,
      }));
    }
  }, [newTraderGuideActive, newTraderGuideStep]);

  // Draggable guide button position - higher on mobile to avoid footer icons
  const [guideButtonPosition, setGuideButtonPosition] = useState(() => {
    const isMobile = window.innerWidth <= 768;
    return {
      bottom: isMobile ? 140 : 16, // 80px up on mobile to clear footer icons
      right: 3
    };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });
  const [hasDraggedSignificantly, setHasDraggedSignificantly] = useState(false);

  // ===== INFINITE SCROLL STATE =====
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingOverlays, setIsLoadingOverlays] = useState(false);
  const [isLoadingIndicators, setIsLoadingIndicators] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  // ===== END INFINITE SCROLL STATE =====

  // Keyboard shortcut to show guide (Ctrl+G or Cmd+G)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'g') {
        event.preventDefault();
        setShowChartGuide(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Draggable guide button handlers
  const handleDragStart = (e) => {
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top
    });

    setDragStartPosition({ x: clientX, y: clientY });
    setHasDraggedSignificantly(false);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // Check if user has dragged significantly (threshold: 10px)
    const dragDistance = Math.sqrt(
      Math.pow(clientX - dragStartPosition.x, 2) +
      Math.pow(clientY - dragStartPosition.y, 2)
    );

    if (dragDistance > 10) {
      setHasDraggedSignificantly(true);
    }

    const newRight = window.innerWidth - clientX - dragOffset.x;
    const newBottom = window.innerHeight - clientY - dragOffset.y;

    // Keep button within screen bounds (48px is button size)
    const clampedRight = Math.max(8, Math.min(newRight, window.innerWidth - 48));
    const clampedBottom = Math.max(8, Math.min(newBottom, window.innerHeight - 48));

    setGuideButtonPosition({ right: clampedRight, bottom: clampedBottom });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    // Reset drag flag after a brief delay to allow click handler to check it
    setTimeout(() => setHasDraggedSignificantly(false), 100);
  };

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleDragMove(e);
      const handleTouchMove = (e) => handleDragMove(e);

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleDragEnd);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, dragOffset]);

  // Clear fromChart flag when user arrives at chart page
  useEffect(() => {
    localStorage.removeItem('fromChart');
  }, []);

  // Use live chart data hook
  const { liveData, currentPrice, previousPrice } = useLiveChartData(tickerFromLocalStorage, setting, data);

  const toggleDropdown1 = (index) => {
    const newDropdownStates = [...dropdownStates1];
    newDropdownStates[index] = !newDropdownStates[index];
    setDropdownStates1(newDropdownStates);
  };


  useEffect(() => {
    const fetchGuideStatus = async () => {
      try {
        const response = await getGuideSetting();

        if (response.data.success && response.data.status === 200) {
          setGuideIcon(response.data.show_icon);
        }
      } catch (error) {
        console.log("Error fetching guide status:", error);
      }
    };

    fetchGuideStatus();
  }, []);

  // Ensure symbol is set on mount - always sync with tickerFromLocalStorage
  useEffect(() => {
    if (tickerFromLocalStorage && setting.symbol !== tickerFromLocalStorage) {
      setSetting(prev => ({
        ...prev,
        symbol: tickerFromLocalStorage
      }));
    }
  }, [tickerFromLocalStorage]);

  useEffect(() => {
    if (localStorage.getItem("tickerEventImage")) {
      setPreview1(true);
    }
  }, []);

  useEffect(() => {
    const handleCustomProfileEvent = (event) => {
      localStorage.setItem("tickerEventImage", event?.detail?.imageChart);
      setPreview1(true);
      setActiveImage1(event?.detail?.imageChart);
    };
    window.addEventListener("tickerEventImage", handleCustomProfileEvent);
  }, [window.addEventListener]);

  const handleShareToFollowers = async (id, index) => {
    setSelectedIndices([...selectedIndices, index]);
    const obj = {
      chart_id: shareChartData1?.id ? shareChartData1?.id : shareChartData?.id,
      users: id,
    };
    const res = await shareSavedChartApi(obj);
    if (res?.data?.status === 200) {
    } else {
      setSelectedIndices(selectedIndices.filter((i) => i !== index));
      toast.error(res?.data?.message);
    }
  };

  const handleShareCancel = () => {
    setIsFollowersOpen(false);
    setSelectedIds([]);
    setSelectedIndices([]);
  };

  const onShareChartClick = (chart) => {
    setShareChartData(chart);
    setIsFollowersOpen(true);
    setPreview(false);
    setSelectedIds([]);
  };

  const shareChartAsPost = async (chart) => {
    setChartId(chart?.id);
    setShowModalChart(true);
  };
  const [inputValue, setInputValue] = useState("");

  const [sharingInProgress, setSharingInProgress] = useState(false);

  const handleShare = async () => {
    // If sharing is already in progress, return early to prevent multiple clicks
    if (sharingInProgress) return;

    setSharingInProgress(true); // Set flag to indicate sharing is in progress

    // Composer stores rich-text HTML; send empty string when blank.
    const description = isQuillEmpty(inputValue) ? "" : inputValue;

    try {
      // Client Rev 3 item 9: "When you share a chart from the trade screen, it
      // posts to the main feed vs the leagues feed. When you click the plus
      // button, it will post to the leagues feed." This screen is shared by the
      // main account and /:slug/trade, and the league it is running inside is
      // published on LeagueTradeContext — the same source the order ticket uses
      // to book trades against the league. Tag the post with it so a chart
      // shared from inside a league lands in that league's feed, exactly like
      // the "+" composer already does. Outside a league `league` is null and the
      // call is unchanged.
      const res = league?.gameId
        ? await shareCartAsPostApi(
            shareChartData1?.id || chartId,
            description,
            league.gameId
          )
        : await shareCartAsPostApi(shareChartData1?.id || chartId, description);
      if (res?.data?.status === 200) {
        setPreview(false);
        setSelectedIds([]);
        setShowModalChart(false);
        toast.success(res?.data?.message);
        setInputValue("");
      } else {
        toast.error("Could not share the chart. Try again.");
      }
    } catch (error) {
      console.error("Error sharing cart:", error);
      toast.error("Could not share the chart. Try again.");
    } finally {
      setSharingInProgress(false); // Reset the flag whether successful or not
    }
  };
  useEffect(() => {
    getFollowersList();
  }, []);

  const getFollowersList = async () => {
    try {
      const res = await getFollowersListApi();
      if (res?.data?.status === 200) {
        setFollowersList(res?.data?.data?.data);
      }
    } catch (err) { }
  };

  useEffect(() => {
    const handleCustomProfileEvent = (event) => {
      if (event?.detail?.ticker) {
        const newTicker = event?.detail?.ticker;
        // Skip if same ticker to prevent duplicate API calls
        if (currentTickerRef.current === newTicker) {
          console.log('[char.jsx] Skip duplicate ticker event:', newTicker);
          return;
        }
        currentTickerRef.current = newTicker;
        setSetting({
          ...setting,
          symbol: newTicker,
          adlValue: "",
          adxValue: "",
          cciValue: "",
          cmfValue: "",
          macdValue: "",
          macdhValue: "",
          mfiValue: "",
          obvValue: "",
          ppoValue: "",
          pvoValue: "",
          rocValue: "",
          efiValue: "",
          stochValue: "",
          rsiValue: "",
          clearIndicator: true,
        });
        setRows([{ selectValue: "", inputValue: "", selectPosValue: "above" }]);
        localStorage.setItem("ticker", newTicker);
        // Update state so re-renders use the new ticker
        setTickerFromLocalStorage(newTicker);
      }
    };
    window.addEventListener("tickerEvent", handleCustomProfileEvent);
    return () => window.removeEventListener("tickerEvent", handleCustomProfileEvent);
  }, [setting]);

  const handlePreview1Close = () => {
    setPreview1(false);
    localStorage.removeItem("tickerEventImage");
  };

  // Ref to track last fetched stock to prevent duplicate API calls
  const lastFetchedStockRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      const stock = tickerFromLocalStorage
        ? tickerFromLocalStorage
        : setting.symbol;

      // Skip if same stock was already fetched
      if (lastFetchedStockRef.current === stock) {
        console.log('[char.jsx] Skip duplicate getChartStockDetails for:', stock);
        return;
      }
      lastFetchedStockRef.current = stock;

      try {
        setloaders(true);
        // const response = await getChartStockDetails(stock);
        const searchString = stock?.toUpperCase() || "SPY";

        const category =
          searchString.startsWith("C:") ? "Forex" :
            searchString.startsWith("X:") ? "Crypto" :
              "Stocks";

        const response = await getChartStockDetails({
          ticker: searchString,
          category: category,
        });
        console.log(response.data.data, 'latest');
        if (response?.data?.data?.ticker?.name) {
          setstockData(response.data.data);
          setloaders(false);
        } else {
          setloaders(false);
        }
      } catch (error) {
        setloaders(false);
        console.error("Error:", error);
      }
    };

    fetchData();
  }, [setting.symbol, tickerFromLocalStorage]);

  const handleInputChange = (event) => {
    setPrice(event.target.value);
  };

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  const handleAPICall = async () => {
    const priceP = price.trim();
    if (!priceP) {
      toast.error("Enter a price for the alert.");
      return;
    }
    setloaders(true);
    const data = {
      stock_symbol: tickerFromLocalStorage
        ? tickerFromLocalStorage
        : setting.symbol,
      notificaion_type: activeTab,
      amount: price.trim(),
    };
    try {
      const response = await addChartNotification(data);
      if (response) {
        setloaders(false);
        setPrice("");
        setIsNotificationOpen(false);
        toast.success("Price alert set.");
      }
    } catch (error) {
      setloaders(false);
      toast.error("Could not create the price alert. Try again.");
      console.error("Error:", error);
    }
  };
  const handleSaveNotification = () => {
    setIsNotificationOpen(false);
  };
  const handleClose = () => {
    setIsNotificationOpen(false);
  };

  const [isWidth, setISWidth] = useState(window.innerWidth < 890);
  useEffect(() => {
    setISWidth(window.innerWidth < 890);
  }, [window.innerWidth]);

  const checkForMobileDevice = () => {
    return /Mobi|Android/i.test(window.navigator.userAgent);
  };



  useEffect(() => {
    // Check if the user is on a mobile device
    if (checkForMobileDevice()) {
      setSetting((prev) => ({ ...prev, isMobile: true }));
    } else {
      setSetting((prev) => ({ ...prev, isMobile: false }));
    }
  }, []);

  const base64toFile = (base64String, filename) => {
    const byteString = atob(base64String.split(",")[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: "image/jpeg" });
    return new File([blob], filename, { type: "image/jpeg" });
  };

  const captureElement_BKP = async () => {
    if (captureRef.current) {
      // console.log(captureRef.current, "capture");
      setloaders(true);
      html2canvas(captureRef.current).then(async (canvas) => {
        const image = canvas.toDataURL("image/png");
        const file = base64toFile(image, "file.jpg");
        const res = await s3SingleUpload(file);
        if (res?.data?.status === 200) {
          const convertedArray = res?.data?.data.map((str) => ({ image: str }));
          const res1 = await saveChart(
            convertedArray,
            tickerFromLocalStorage ? tickerFromLocalStorage : setting.symbol
          );
          if (res1?.data?.status === 200) {
            setloaders(false);
            toast.success(res1.data.message);
            fetchSavedCharts();
          } else {
            setloaders(false);
            toast.error(res1.data.message);
          }
        } else {
          setloaders(false);
          toast.error(res.data.message);
        }
      });
    }
  };

  const setLegendItemsMargins = (isSet = false) => {
    const textElements = document.querySelectorAll(".legend-item-text");
    const colorElements = document.querySelectorAll(".legend-item-color");

    if (isSet) {
      textElements.forEach((element) => {
        element.style.marginTop = "-10.5px"; // Adjust the value as needed
      });

      colorElements.forEach((element) => {
        element.style.marginTop = "2px"; // Adjust the value as needed
      });
    } else {
      textElements.forEach((element) => {
        element.style.marginTop = ""; // Adjust the value as needed
      });

      colorElements.forEach((element) => {
        element.style.marginTop = ""; // Adjust the value as needed
      });
    }
  };

  // const captureElement = async () => {
  //   if (captureRef.current) {
  //     // console.log(captureRef.current, "capture");
  //     setloaders(true);
  //     setLegendItemsMargins(true);
  //     html2canvas(captureRef.current).then(async (canvas) => {
  //       const image = canvas.toDataURL("image/png");
  //       const file = base64toFile(image, "file.jpg");
  //       const res = await s3SingleUpload(file);
  //       setLegendItemsMargins();
  //       if (res?.data?.status === 200) {
  //         const convertedArray = res?.data?.data.map((str) => ({ image: str }));
  //         const res1 = await saveChart(
  //           convertedArray,
  //           tickerFromLocalStorage ? tickerFromLocalStorage : setting.symbol
  //         );
  //         if (res1?.data?.status === 200) {
  //           setloaders(false);
  //           toast.success(res1.data.message);
  //           fetchSavedCharts();
  //         } else {
  //           setloaders(false);
  //           toast.error(res1.data.message);
  //         }
  //       } else {
  //         setloaders(false);
  //         toast.error(res.data.message);
  //       }
  //     });
  //   }
  // };

  const captureElement = async () => {
    if (captureRef.current) {
      setloaders(true);
      setHideZoomControls(true);

      // Small delay to allow React to re-render and hide zoom controls
      await new Promise(resolve => setTimeout(resolve, 100));

      // Pass the full container
      html2canvas(captureRef.current, {
        useCORS: true,   // important if any images/icons
        backgroundColor: "white", // avoids transparency
        scale: 2         // improves quality
      }).then(async (canvas) => {
        const image = canvas.toDataURL("image/png");
        const file = base64toFile(image, "file.jpg");
        console.log(file, "file we are getting is.....");
        const res = await s3SingleUpload(file);
        setLegendItemsMargins();
        setHideZoomControls(false);

        if (res?.data?.status === 200) {
          const convertedArray = res?.data?.data.map((str) => ({ image: str }));
          const res1 = await saveChart(
            convertedArray,
            tickerFromLocalStorage ? tickerFromLocalStorage : setting.symbol
          );

          if (res1?.data?.status === 200) {
            setloaders(false);
            toast.success(res1.data.message);
            fetchSavedCharts();
          } else {
            setloaders(false);
            toast.error(res1.data.message);
          }
        } else {
          setloaders(false);
          toast.error(res.data.message);
        }
      });
    }
  };

  // Direct share chart - captures and immediately opens share modal (without saving to saved charts or S3)
  const [directShareImageFile, setDirectShareImageFile] = useState(null);
  const [showDirectShareModal, setShowDirectShareModal] = useState(false);
  const [directShareDescription, setDirectShareDescription] = useState("");
  const [directShareInProgress, setDirectShareInProgress] = useState(false);

  const directShareChart = async () => {
    if (captureRef.current) {
      setloaders(true);
      setLegendItemsMargins(true);
      setHideZoomControls(true);

      // Small delay to allow React to re-render and hide zoom controls
      await new Promise(resolve => setTimeout(resolve, 100));

      html2canvas(captureRef.current, {
        useCORS: true,
        backgroundColor: "white",
        scale: 2
      }).then(async (canvas) => {
        const image = canvas.toDataURL("image/png");
        const file = base64toFile(image, "chart.png");
        setLegendItemsMargins();
        setHideZoomControls(false);

        // Store the file and open modal (no S3 upload)
        setDirectShareImageFile(file);
        setloaders(false);
        setShowDirectShareModal(true);
      });
    }
  };

  useEffect(() => {
    const handler = () => {
      directShareChart();
    };
    window.addEventListener("qushi:open-share-modal", handler);
    return () => window.removeEventListener("qushi:open-share-modal", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDirectShare = async () => {
    if (directShareInProgress || !directShareImageFile) return;
    setDirectShareInProgress(true);

    try {
      // Send image file directly - backend will save to local storage
      const formData = new FormData();
      formData.append('description', isQuillEmpty(directShareDescription) ? '' : directShareDescription);
      formData.append('caption', '');
      formData.append('tag', '');
      formData.append('post_status', '1');
      formData.append('post_images[]', directShareImageFile);
      // Client Rev 5: "When you share a chart from the trade screen, it posts to
      // the main feed rather than the league's feed. When you click the plus
      // button, it will post to the league's feed."
      //
      // There are TWO share paths on this screen and Rev 3 only fixed the other
      // one (handleShare, for a chart already saved to Saved Charts). THIS is the
      // path the Trade screen's share control actually uses — it captures the
      // canvas and posts it straight through create-update-post — and it was
      // sending no game_id at all, so every chart landed in the main feed.
      //
      // `league` is the same LeagueTradeContext the order ticket books trades
      // against, and create-update-post already accepts game_id (it is what the
      // "+" composer sends). Outside a league this appends nothing and the call
      // is byte-for-byte what it was.
      if (league?.gameId) {
        formData.append('game_id', league.gameId);
      }

      const response = await createPost(formData);
      if (response?.status === 200) {
        setShowDirectShareModal(false);
        toast.success(
          response?.data?.data?.moderation_status === "pending_review"
            ? "Post submitted for review."
            : "Post published."
        );
        setDirectShareDescription("");
        setDirectShareImageFile(null);
      } else {
        toast.error("Could not share the chart. Try again.");
      }
    } catch (error) {
      console.error("Error sharing chart:", error);
      toast.error("Could not share the chart. Try again.");
    } finally {
      setDirectShareInProgress(false);
    }
  };

  // Save-to-Watchlist modal. Reads get-user-watchlists — the SAME source the
  // Watchlist screens list and delete against — so a list deleted there is gone
  // here too. (The old get-watchlists call returned every non-system row the
  // user owned, including pre-redesign lists with no parent, which is why lists
  // that can't be deleted on the Watchlist screens still showed up in here.)
  const handleAddToWatchlist = async () => {
    try {
      setLoading1(true); // show loader
      const res = await getUserWatchlists();
      const lists = Array.isArray(res?.data?.watchlists) ? res.data.watchlists : [];
      setWatchlists(lists);
      // Pre-fill checkboxes with false
      const init = {};
      lists.forEach(w => { init[w.id] = false });
      setSelected1(init);
      setShowModal1(true);
    } catch (err) {
      console.error("Error fetching watchlists:", err);
    } finally {
      setLoading1(false); // hide loader
    }
  };


  const handleCheckboxChange = (id) => {
    setSelected1(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleConfirm = async () => {
    const chosenIds = Object.keys(selected1).filter(id => selected1[id]);
    const symbol = tickerFromLocalStorage
      ? tickerFromLocalStorage
      : setting.symbol;

    // "Done" with nothing checked is a plain dismiss — the add endpoint rejects
    // an empty id list, so don't call it just to show an error.
    if (chosenIds.length === 0) {
      setShowModal1(false);
      return;
    }

    console.log("Chosen Watchlists:", chosenIds);
    console.log("Symbol to add:", symbol);

    try {
      setLoading1(true); // show loader
      const res = await addStockToWatchlists(symbol, chosenIds);
      console.log("API Response:", res);

      // Show success toast if API response is successful
      // Note: addStockToWatchlists returns response.data directly
      if (res?.status === 200) {
        toast.success("Added to watchlist.");
      } else if (res?.message) {
        toast.error("Could not add the asset to this watchlist. Try again.");
      }
    } catch (err) {
      console.error("Failed to add stock:", err);
      toast.error("Could not add the asset to this watchlist. Try again.");
    } finally {
      setLoading1(false); // hide loader
      setShowModal1(false);
    }
  };



  const {
    fetchBollingerData,
    fetchPsarData,
    fetchSmaData,
    fetchEmaData,
    fetchAdlData,
    fetchAdxData,
    fetchRSIData,
    fetchStochData,
    fetchMacdhData,
    fetchCCIData,
    fetchPPOData,
    fetchCMFData,
    fetchPVOData,
    fetchMACDData,
    fetchMFIData,
    fetchROCData,
    fetchOBVData,
    fetchEFIData,
  } = fetchChartsData(
    tickerFromLocalStorage,
    setting,
    setOverlays,
    setOverlayloader,
    seterror,
    setIndicators,
    setIndicatorloader
  );

  useEffect(() => {
    const fetchOverlayData = async () => {
      if (isOverlaySubmit) {
        setIsOverlaySubmit(!isOverlaySubmit);
        try {
          for (const item of setting.overlayParams) {
            let index = setting.overlayParams.indexOf(item);
            if (
              item.inputValue === undefined ||
              item.inputValue === "" ||
              item.selectValue === ""
            ) {
              continue;
            }
            setOverlayloader(true);
            if (item.selectValue === "bb" && item.inputValue !== "") {
              fetchBollingerData(item, index);
            }
            if (item.selectValue === "sma" && item.inputValue !== "") {
              fetchSmaData(item, index);
            }
            if (item.selectValue === "ema" && item.inputValue !== "") {
              fetchEmaData(item, index);
            }
            if (item.selectValue === "psar" && item.inputValue !== "") {
              fetchPsarData(item, index);
            }
          }
        } catch (error) {
          toast.error("Could not load this indicator. Try again.");
          setOverlayloader(false);
          seterror(error?.message);
          console.error("Error fetching Bollinger Bands data:", error);
        }
      }
    };

    fetchOverlayData();
  }, [isOverlaySubmit, isParamsSubmit]);

  useEffect(() => {
    const fetchIndicatorData = async () => {
      for (const item of setting.indicatorParams) {
        await sleep(250);
        let index = setting.indicatorParams.indexOf(item);
        if (
          item.inputValue === undefined ||
          item.inputValue === "" ||
          item.selectValue === ""
        ) {
          continue;
        }
        setIndicatorloader(true);

        if (item.selectValue === "adl" && item.inputValue !== "") {
          fetchAdlData(item, index);
        }
        if (item.selectValue === "macdh" && item.inputValue !== "") {
          fetchMacdhData(item, index);
        }
        if (item.selectValue === "stoch" && item.inputValue !== "") {
          fetchStochData(item, index);
        }
        if (item.selectValue === "rsi" && item.inputValue !== "") {
          fetchRSIData(item, index);
        }
        if (item.selectValue === "adx" && item.inputValue !== "") {
          fetchAdxData(item, index);
        }
        if (item.selectValue === "cci" && item.inputValue !== "") {
          fetchCCIData(item, index);
        }
        if (item.selectValue === "macd" && item.inputValue !== "") {
          fetchMACDData(item, index);
        }
        if (item.selectValue === "mfi" && item.inputValue !== "") {
          fetchMFIData(item, index);
        }
        if (item.selectValue === "obv" && item.inputValue !== "") {
          fetchOBVData(item, index);
        }
        if (item.selectValue === "ppo" && item.inputValue !== "") {
          fetchPPOData(item, index);
        }
        if (item.selectValue === "efi" && item.inputValue !== "") {
          fetchEFIData(item, index);
        }
        if (item.selectValue === "roc" && item.inputValue !== "") {
          fetchROCData(item, index);
        }
        if (item.selectValue === "pvo" && item.inputValue !== "") {
          fetchPVOData(item, index);
        }
        if (item.selectValue === "cmf" && item.inputValue !== "") {
          fetchCMFData(item, index);
        }
      }
    };
    fetchIndicatorData();
  }, [isIndicatorSubmit, isParamsSubmit]);

  useEffect(() => {
    const fetchIndicatorData = async () => {
      let updateList = [];
      try {
        for (const item of setting.indicatorParams) {
          let index = setting.indicatorParams.indexOf(item);
          if (
            item.inputValue === undefined ||
            item.inputValue === "" ||
            item.selectValue === ""
          ) {
            continue;
          }
          let indicatorItem = {
            id: "",
            yAccessor: "",
            strokeStyle: "",
            yExtents: "",
            position: "",
            lineConfig: [],
            // The parameter string the user typed for this indicator ("14" for
            // RSI, "12,26,9" for MACD, "0" for the ones that take none). Carried
            // through so the panel label can read `RSI (14)` — client, Indicator
            // Polish 1: "Keep the parameter only when meaningful, such as
            // `RSI (14)`. Display only `ADL` if its `0` parameter has no
            // purpose." Chart.js decides which of those two a value is; this
            // just makes the value available to it.
            params: item.inputValue,
          };
          if (item.selectValue === "adl" && item.inputValue !== "") {
            indicatorItem.id = "ADL";
            indicatorItem.data = indicators?.[index]?.data;
            indicatorItem.yAccessor = (d) => d?.indicators[index]?.adl?.ad;
            indicatorItem.yExtents = (d) => d?.indicators[index]?.adl?.ad;
            indicatorItem.strokeStyle = "#ff7f0e";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: true,
                yAccessor: (d) => d?.indicators[index]?.adl?.ad,
                strokeStyle: "#ff7f0e",
              },
            ];
            updateList.push(indicatorItem);
          }
          if (item.selectValue === "macdh" && item.inputValue !== "") {
            indicatorItem.id = "MACDH";
            indicatorItem.data = indicators?.[index]?.data;
            // Parse fast,slow,signal from inputValue (e.g. "12,26,9")
            if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
              const parts = item.inputValue.split(',').map(s => parseInt(s.trim(), 10));
              indicatorItem.fast = parts[0] || 12;
              indicatorItem.slow = parts[1] || 26;
              indicatorItem.signal = parts[2] || 9;
            }
            indicatorItem.yAccessor = (d) => d.indicators[index]?.macdh?.macdh;
            indicatorItem.yExtents = (d) => d.indicators[index]?.macdh?.macdh;
            indicatorItem.strokeStyle = "#ff7f0e";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: false,
                yAccessor: (d) => d.indicators[index].macdh?.macdh,
                strokeStyle: "#ff7f0e",
              },
            ];
            updateList.push(indicatorItem);
          }
          if (item.selectValue === "stoch" && item.inputValue !== "") {
            indicatorItem.id = "STOCH";
            indicatorItem.data = indicators?.[index]?.data;
            indicatorItem.kPeriod = parseInt(item.inputValue, 10) || 14;
            indicatorItem.yAccessor = (d) => d.indicators[index]?.stoch?.stoch;
            indicatorItem.yExtents = (d) => d.indicators[index]?.stoch?.stoch;
            indicatorItem.strokeStyle = "#ff7f0e";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.stoch?.stoch,
                strokeStyle: "#ff7f0e",
              },
            ];
            updateList.push(indicatorItem);
          }
          if (item.selectValue === "rsi" && item.inputValue !== "") {
            indicatorItem.id = "RSI";
            indicatorItem.data = indicators?.[index]?.data;
            indicatorItem.windowSize = parseInt(item.inputValue, 10) || 14;
            indicatorItem.yAccessor = (d) => d.indicators[index]?.rsi?.rsi;
            indicatorItem.yExtents = (d) => d.indicators[index]?.rsi?.rsi;
            indicatorItem.strokeStyle = "#ff7f0e";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.rsi?.rsi,
                strokeStyle: "#ff7f0e",
              },
            ];
            updateList.push(indicatorItem);
          }
          if (item.selectValue === "adx" && item.inputValue !== "") {
            indicatorItem.id = "ADX";
            indicatorItem.data = indicators?.[index]?.data;
            indicatorItem.windowSize = parseInt(item.inputValue, 10) || 14;
            indicatorItem.yAccessor = (d) => d.indicators[index]?.adl?.adx;
            indicatorItem.yExtents = (d) => [
              Math.min(
                d.indicators[index]?.adx?.adx,
                d.indicators[index]?.adx?.dmn,
                d.indicators[index]?.adx?.dmp
              ),
              Math.max(
                d.indicators[index]?.adx?.adx,
                d.indicators[index]?.adx?.dmn,
                d.indicators[index]?.adx?.dmp
              ),
            ];
            indicatorItem.strokeStyle = "#2ca02c";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.adx?.adx,
                strokeStyle: "#00FF00",
              },
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.adx?.dmn,
                strokeStyle: "#0000FF",
              },
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.adx?.dmp,
                strokeStyle: "#FF0000",
              },
            ];
            updateList.push(indicatorItem);
          }
          if (item.selectValue === "cci" && item.inputValue !== "") {
            indicatorItem.id = "CCI";
            indicatorItem.data = indicators?.[index]?.data;
            indicatorItem.windowSize = parseInt(item.inputValue, 10) || 20;
            indicatorItem.yAccessor = (d) => d.indicators[index]?.cci?.cci;
            indicatorItem.yExtents = (d) => d.indicators[index]?.cci?.cci;
            indicatorItem.strokeStyle = "#FF00FF";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.cci?.cci,
                strokeStyle: "#FF00FF",
              },
            ];
            updateList.push(indicatorItem);
          }

          if (item.selectValue === "fi" && item.inputValue !== "") {
            setting.enableSSAR = true;
          }
          if (item.selectValue === "macd" && item.inputValue !== "") {
            indicatorItem.id = "MACD";
            indicatorItem.data = indicators?.[index]?.data;
            // Parse fast,slow,signal from inputValue (e.g. "12,26,9")
            if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
              const parts = item.inputValue.split(',').map(s => parseInt(s.trim(), 10));
              indicatorItem.fast = parts[0] || 12;
              indicatorItem.slow = parts[1] || 26;
              indicatorItem.signal = parts[2] || 9;
            }
            indicatorItem.yAccessor = (d) => d.indicators[index].macd.macd;
            indicatorItem.yExtents = (d) => [
              Math.min(
                d.indicators[index]?.macd?.macd,
                d.indicators[index]?.macd?.macdh,
                d.indicators[index]?.macd.macds
              ),
              Math.max(
                d.indicators[index]?.macd?.macd,
                d.indicators[index]?.macd?.macdh,
                d.indicators[index]?.macd.macds
              ),
            ];
            indicatorItem.strokeStyle = "#FFC0CB";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.macd?.macd,
                strokeStyle: "#00FF00",
              },

              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.macd?.macds,
                strokeStyle: "#FF0000",
              },
            ];
            updateList.push(indicatorItem);
          }
          if (item.selectValue === "mfi" && item.inputValue !== "") {
            indicatorItem.id = "MFI";
            indicatorItem.data = indicators?.[index]?.data;
            indicatorItem.windowSize = parseInt(item.inputValue, 10) || 14;
            indicatorItem.yAccessor = (d) => d.indicators[index]?.mfi?.mfi;
            indicatorItem.yExtents = (d) => d.indicators[index]?.mfi?.mfi;
            indicatorItem.strokeStyle = "#FF0000";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.mfi?.mfi,
                strokeStyle: "#FF0000",
              },
            ];
            updateList.push(indicatorItem);
          }
          if (item.selectValue === "obv" && item.inputValue !== "") {
            indicatorItem.id = "OBV";
            indicatorItem.data = indicators?.[index]?.data;
            indicatorItem.yAccessor = (d) => d.indicators[index]?.obv?.obv;
            indicatorItem.yExtents = (d) => d.indicators[index]?.obv?.obv;
            indicatorItem.strokeStyle = "#800080";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.obv?.obv,
                strokeStyle: "#800080",
              },
            ];
            updateList.push(indicatorItem);
          }
          if (item.selectValue === "ppo" && item.inputValue !== "") {
            indicatorItem.id = "PPO";
            indicatorItem.data = indicators?.[index]?.data;
            if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
              const parts = item.inputValue.split(',').map(s => parseInt(s.trim(), 10));
              indicatorItem.fast = parts[0] || 12;
              indicatorItem.slow = parts[1] || 26;
              indicatorItem.signal = parts[2] || 9;
            }
            indicatorItem.yAccessor = (d) => d.indicators[index]?.ppo?.ppo;
            indicatorItem.yExtents = (d) => d.indicators[index]?.ppo?.ppo;
            indicatorItem.strokeStyle = "#FF0000";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.ppo?.ppo,
                strokeStyle: "#FF0000",
              },
            ];
            updateList.push(indicatorItem);
          }
          if (item.selectValue === "efi" && item.inputValue !== "") {
            indicatorItem.id = "EFI";
            indicatorItem.data = indicators?.[index]?.data;
            indicatorItem.windowSize = parseInt(item.inputValue, 10) || 13;
            indicatorItem.yAccessor = (d) => d.indicators[index]?.efi?.efi;
            indicatorItem.yExtents = (d) => d.indicators[index]?.efi?.efi;
            indicatorItem.strokeStyle = "#0000FF";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.efi?.efi,
                strokeStyle: "#0000FF",
              },
            ];
            updateList.push(indicatorItem);
          }
          if (item.selectValue === "roc" && item.inputValue !== "") {
            indicatorItem.id = "ROC";
            indicatorItem.data = indicators?.[index]?.data;
            indicatorItem.windowSize = parseInt(item.inputValue, 10) || 12;
            indicatorItem.yAccessor = (d) => d.indicators[index]?.roc?.roc;
            indicatorItem.yExtents = (d) => d.indicators[index]?.roc?.roc;
            indicatorItem.strokeStyle = "#2ca02c";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.roc?.roc,
                strokeStyle: "#2ca02c",
              },
            ];
            updateList.push(indicatorItem);
          }
          if (item.selectValue === "pvo" && item.inputValue !== "") {
            indicatorItem.id = "PVO";
            indicatorItem.data = indicators?.[index]?.data;
            if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
              const parts = item.inputValue.split(',').map(s => parseInt(s.trim(), 10));
              indicatorItem.fast = parts[0] || 12;
              indicatorItem.slow = parts[1] || 26;
              indicatorItem.signal = parts[2] || 9;
            }
            indicatorItem.yAccessor = (d) => d.indicators[index]?.pvo?.pvo;
            indicatorItem.yExtents = (d) => d.indicators[index]?.pvo?.pvo;
            indicatorItem.strokeStyle = "#FFA500";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.pvo?.pvo,
                strokeStyle: "#FFA500",
              },
            ];
            updateList.push(indicatorItem);
          }
          if (item.selectValue === "cmf" && item.inputValue !== "") {
            indicatorItem.id = "CMF";
            indicatorItem.data = indicators?.[index]?.data;
            indicatorItem.windowSize = parseInt(item.inputValue, 10) || 20;
            indicatorItem.yAccessor = (d) => d.indicators[index]?.cmf?.cmf;
            indicatorItem.yExtents = (d) => d.indicators[index]?.cmf?.cmf;
            indicatorItem.strokeStyle = "#00FF00";
            indicatorItem.position = item.selectPosValue;
            indicatorItem.lineConfig = [
              {
                lineType: true,
                yAccessor: (d) => d.indicators[index]?.cmf?.cmf,
                strokeStyle: "#00FF00",
              },
            ];
            updateList.push(indicatorItem);
          }
        }
        setIndicatorConfig(updateList);
      } catch (error) {
        toast.error("Could not load this indicator. Try again.");
        setIndicatorloader(false);
        seterror(error?.message);
        console.error("Error fetching indicator data:", error);
      }
    };

    fetchIndicatorData();
  }, [indicators]);

  // Symbols whose pre-listing data from the provider is wrong and must be hidden.
  // Add entries as the provider flags more tickers.
  const SYMBOL_MIN_DATES = {
    COIN: '2021-04-11', // Coinbase listed 2021-04-14; earlier bars are junk
  };

  const getSymbolMinDate = (symbol) => SYMBOL_MIN_DATES[symbol?.toUpperCase()] || null;

  const isBarOnOrAfterSymbolMinDate = (symbol, bar) => {
    const minDate = getSymbolMinDate(symbol);
    if (!minDate) return true;
    return bar.date.getTime() >= new Date(minDate).getTime();
  };

  // META was renamed from FB on 2022-06-09
  // For data before 2022-06-01, use FB symbol
  const META_FB_CUTOFF_DATE = '2022-06-01';
  // UTC timestamp for June 1, 2022 00:00:00 - used for filtering bars
  const META_FB_CUTOFF_TIMESTAMP = Date.UTC(2022, 5, 1); // Month is 0-indexed, so 5 = June
  // META's lowest price was around $88 in Nov 2022, so any bar below $50 is definitely wrong FB data
  const META_MIN_VALID_PRICE = 50;

  // Helper function to check if symbol is META
  const isMetaSymbol = (symbol) => symbol?.toUpperCase() === 'META';

  // Helper function to check if a bar has valid META data (not old FB data)
  const isValidMetaBar = (bar) => {
    // Check both timestamp AND price - old FB data has very low prices (~15-17)
    const hasValidDate = bar.date.getTime() >= META_FB_CUTOFF_TIMESTAMP;
    const hasValidPrice = bar.close >= META_MIN_VALID_PRICE && bar.open >= META_MIN_VALID_PRICE;
    return hasValidDate && hasValidPrice;
  };

  // Helper function to get the correct symbol for historical data before 2022-06-01
  const getHistoricalSymbol = (symbol, endDate) => {
    if (isMetaSymbol(symbol) && endDate) {
      const cutoffDate = new Date(META_FB_CUTOFF_DATE);
      const requestDate = new Date(endDate);
      if (requestDate <= cutoffDate) {
        console.log('[Char] Using FB instead of META for historical data at or before 2022-06-01');
        return 'FB';
      }
    }
    return symbol;
  };

  // ===== INFINITE SCROLL: Load more historical data =====
  const loadMoreHistoricalData = async () => {
    if (!hasMoreData || isLoadingMore || isFetchingRef.current || data.length === 0) {
      console.log('[Char] Skip load more:', { hasMoreData, isLoadingMore, dataLength: data.length });
      return;
    }

    // Debounce: prevent rapid fetches
    const now = Date.now();
    if (now - lastFetchTimeRef.current < 1000) {
      console.log('[Char] Debouncing fetch');
      return;
    }

    console.log('[Char] Loading more historical data...');
    setIsLoadingMore(true);
    isFetchingRef.current = true;
    lastFetchTimeRef.current = now;

    try {
      const sym = tickerFromLocalStorage || setting.symbol;
      // Find period by matching either period code ("1h") or internal name ("hour") with multiplier
      const selectedPeriodObj = periods.find(p =>
        p.period === setting.period ||
        (p.internal === setting.period && p.multiplier === setting.multiplier)
      );
      const apiPeriod = selectedPeriodObj ? selectedPeriodObj.internal : setting.period || 'minute';

      // Get oldest timestamp and format as YYYY-MM-DD
      const oldestBar = data[0];
      if (!oldestBar || !oldestBar.date) {
        throw new Error('Cannot determine oldest date');
      }

      const oldestTimestamp = new Date(oldestBar.date).getTime();
      let endDate = formatTimestampForApi(oldestTimestamp);

      // Per-symbol min-date guard: if we've already reached the listing date,
      // stop loading older bars (the provider would return junk anyway).
      const symbolMinDateForLoadMore = getSymbolMinDate(sym);
      if (symbolMinDateForLoadMore &&
        oldestTimestamp <= new Date(symbolMinDateForLoadMore).getTime()) {
        console.log(`[Char] Reached min date ${symbolMinDateForLoadMore} for ${sym} - no more historical data`);
        setHasMoreData(false);
        return;
      }

      // Get the correct symbol for historical data (META -> FB before 2022-06-01)
      const historicalSymbol = getHistoricalSymbol(sym, endDate);

      // If we're switching from META to FB, use the cutoff date as end_date
      // This ensures we get FB data starting from just before 2022-06-01
      if (isMetaSymbol(sym) && historicalSymbol === 'FB') {
        endDate = META_FB_CUTOFF_DATE;
        console.log('[Char] Switching to FB - using cutoff date as end_date:', endDate);
      }

      console.log('[Char] Fetching data before:', endDate, 'using symbol:', historicalSymbol);

      // Fetch 100 previous bars
      const previousData = await getCandlestickData(
        historicalSymbol,
        undefined,
        apiPeriod,
        setting?.multiplier,
        endDate,  // end_date parameter
        100       // limit parameter
      );

      if (!previousData || !previousData.Date || previousData.Date.length === 0) {
        console.log('[Char] No more historical data available');
        setHasMoreData(false);
        return;
      }

      // Transform and merge data
      let formattedPrevious = transformApiDataToChartFormat(previousData);

      // For META symbol fetching META data, filter out any invalid bars (old FB data) before merging
      // Don't filter when we're fetching FB data - FB prices are legitimately low
      if (isMetaSymbol(sym) && historicalSymbol === 'META') {
        const beforeFilter = formattedPrevious.length;
        formattedPrevious = formattedPrevious.filter(bar => isValidMetaBar(bar));
        const afterFilter = formattedPrevious.length;
        if (beforeFilter !== afterFilter) {
          console.log(`[Char] Filtered out ${beforeFilter - afterFilter} invalid META bars from historical data`);
        }

        // If all data was filtered out and we were using META, fetch FB data instead
        if (afterFilter === 0 && beforeFilter > 0) {
          console.log('[Char] All META data was invalid - fetching FB data instead');

          // Fetch FB data with cutoff date
          const fbData = await getCandlestickData(
            'FB',
            undefined,
            apiPeriod,
            setting?.multiplier,
            META_FB_CUTOFF_DATE,
            100
          );

          if (fbData && fbData.Date && fbData.Date.length > 0) {
            formattedPrevious = transformApiDataToChartFormat(fbData);
            console.log(`[Char] Got ${formattedPrevious.length} FB bars`);
          } else {
            console.log('[Char] No FB data available');
            setHasMoreData(false);
            return;
          }
        }
      }

      // Defensive per-symbol min-date filter on historical batches.
      if (symbolMinDateForLoadMore) {
        const beforeFilter = formattedPrevious.length;
        formattedPrevious = formattedPrevious.filter(bar =>
          isBarOnOrAfterSymbolMinDate(sym, bar)
        );
        const afterFilter = formattedPrevious.length;
        if (beforeFilter !== afterFilter) {
          console.log(`[Char] Filtered out ${beforeFilter - afterFilter} pre-listing bars for ${sym} from historical data`);
        }
        if (afterFilter === 0) {
          setHasMoreData(false);
          return;
        }
      }

      const mergedData = mergeChartData(data, formattedPrevious);

      // Row budget for every overlay/indicator re-fetch below.
      //
      // Client: "add the Bollinger Band and the simple moving average, then
      // scroll out. The lines will eventually disappear from the current bars."
      //
      // The indicator service (qushi-chart) does this once a start_date is
      // supplied — overlays_controller.calculate_sma and df_util.cleandffinal
      // both:
      //
      //     df = df[df['timestamp'] >= filter_ts]
      //     output = df.head(limit)        # OLDEST `limit` rows
      //
      // head(), not tail(). So a limit smaller than the number of rows the
      // service holds in [start_date, now] silently drops the NEWEST rows —
      // exactly the symptom, the line stopping short of the live candles.
      // Measured against the live SMA endpoint (AAPL, hour, length 150):
      //   start=2026-06-01 limit=300   -> 2026-06-01 .. 2026-06-26  (10 weeks short)
      //   start=2026-06-01 limit=2000  -> 2026-06-01 .. 2026-09-03  (reaches the last bar)
      //
      // We were passing `mergedData.length`. That is a LOWER bound on the
      // service's row count for the same span, never an upper one — the chart
      // drops pre-listing bars and non-trading gaps the service keeps — so the
      // two counts diverge further with every page of history loaded, and the
      // whole shortfall comes off the newest end.
      //
      // Over-asking is free: the start_date filter runs BEFORE head(), so a
      // generous limit returns the whole [start_date, now] window and nothing
      // beyond it. The floor covers an early pan where mergedData is still small
      // while the service's own window is already wide.
      //
      // Applied to the indicator re-fetches too, not just the overlays the
      // client named: cleandffinal is the same code path, so RSI/ADL/… were
      // being truncated identically. Leaving them short would also desync the
      // panels horizontally from the price chart.
      //
      // The number itself lives in chartDataHelpers so this file and the
      // first-load fetches in fetchChartsData.js cannot drift apart — ADL was
      // missed here once already and shipped truncated.
      const overlayRowBudget = indicatorRowBudget(mergedData.length);

      console.log('[Char] Merged data:', mergedData.length, 'bars');
      console.log('[Char] First bar:', mergedData[0]?.date, 'Last bar:', mergedData[mergedData.length - 1]?.date);

      // NOTE: Do NOT call setData here - wait until overlays AND indicators are merged
      // to avoid showing flat/empty indicators while loading

      // ===== RE-FETCH OVERLAY DATA FOR EXTENDED DATE RANGE =====
      // If overlays are active, re-fetch them for the full date range so they cover historical data
      if (setting.overlayParams && setting.overlayParams.length > 0) {
        const hasActiveOverlays = setting.overlayParams.some(
          item => item.selectValue && item.selectValue !== '' && item.inputValue && item.inputValue !== ''
        );

        if (hasActiveOverlays) {
          console.log('[Char] Re-fetching overlay data for extended date range...');
          setIsLoadingOverlays(true);

          // Calculate the full date range (oldest bar to now)
          const oldestDate = mergedData[0]?.date;
          const startDateStr = oldestDate ? formatTimestampForApi(new Date(oldestDate).getTime()) : undefined;

          const selectedPeriodForOverlay = periods.find(p =>
            p.period === setting.period ||
            (p.internal === setting.period && p.multiplier === setting.multiplier)
          );
          const overlayApiPeriod = selectedPeriodForOverlay ? selectedPeriodForOverlay.internal : setting.period || 'minute';
          const overlaySymbol = tickerFromLocalStorage || setting.symbol;

          // Re-fetch each active overlay with the extended date range
          const overlayPromises = setting.overlayParams.map(async (item, index) => {
            if (!item.selectValue || item.selectValue === '' || !item.inputValue || item.inputValue === '') {
              return null;
            }

            try {
              let overlayData = null;

              switch (item.selectValue) {
                case 'bb': {
                  // Parse Bollinger params ("20,2" format)
                  let length = item.inputValue;
                  let stdDev = 2;
                  if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
                    const parts = item.inputValue.split(',').map(s => s.trim());
                    length = Number(parts[0]);
                    stdDev = Number(parts[1]) || 2;
                  }
                  const bbResp = await getBollingerBandsDataNew1(
                    overlaySymbol, overlayApiPeriod, length, stdDev,
                    setting?.multiplier, startDateStr, undefined, overlayRowBudget
                  );
                  overlayData = { type: 'bb', data: bbResp?.data?.data || bbResp?.data || bbResp };
                  break;
                }
                case 'sma': {
                  const smaResp = await getSMADataNew1(
                    overlaySymbol, overlayApiPeriod, item.inputValue,
                    setting?.multiplier, startDateStr, undefined, overlayRowBudget
                  );
                  overlayData = { type: 'sma', data: smaResp?.data?.data || smaResp?.data || smaResp };
                  break;
                }
                case 'ema': {
                  const emaResp = await getEMADataNew1(
                    overlaySymbol, overlayApiPeriod, item.inputValue,
                    setting?.multiplier, startDateStr, undefined, overlayRowBudget
                  );
                  overlayData = { type: 'ema', data: emaResp?.data?.data || emaResp?.data || emaResp };
                  break;
                }
                case 'psar': {
                  let initAccel = 0.02;
                  let accel = 0.02;
                  if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
                    const parts = item.inputValue.split(',').map(s => s.trim());
                    initAccel = Number(parts[0]) || 0.02;
                    accel = Number(parts[1]) || 0.02;
                  }
                  const psarResp = await getPsarDataNew1(
                    overlaySymbol, overlayApiPeriod, initAccel, accel,
                    setting?.multiplier, startDateStr, undefined, overlayRowBudget
                  );
                  overlayData = { type: 'psar', data: psarResp?.data?.data || psarResp?.data || psarResp };
                  break;
                }
              }

              if (overlayData) {
                // Clear cached timestamp map so it gets rebuilt with new data
                delete overlayData._timestampMap;
                return { index, data: overlayData };
              }
            } catch (overlayErr) {
              console.warn(`[Char] Failed to re-fetch overlay ${item.selectValue}:`, overlayErr);
            }
            return null;
          });

          // Wait for all overlay fetches
          const overlayResults = await Promise.allSettled(overlayPromises);
          const activeOverlays = [];

          overlayResults.forEach(result => {
            if (result.status === 'fulfilled' && result.value) {
              // result.value is { index, data: overlayData }
              activeOverlays.push({ index: result.value.index, overlay: result.value.data });
            }
          });

          setIsLoadingOverlays(false);
          if (activeOverlays.length > 0) {
            // Merge overlay data directly into mergedData using timestamp mapping
            // This avoids calling setOverlays(), which would trigger the main useEffect and reset the chart
            mergedData.forEach((bar) => {
              if (!bar.chart) bar.chart = [];
              const barTimestamp = new Date(bar.date).getTime();

              activeOverlays.forEach(({ index: i, overlay }) => {
                if (!overlay || !overlay.type || !overlay.data) return;

                // Resolve normalized overlay data
                const oData = overlay.data?.BBU || overlay.data?.SMA || overlay.data?.EMA || overlay.data?.Date
                  ? overlay.data
                  : (overlay.data?.data?.BBU || overlay.data?.data?.SMA || overlay.data?.data?.EMA || overlay.data?.data?.Date
                    ? overlay.data.data
                    : overlay.data);

                // Build timestamp map if not exists
                if (!overlay._timestampMap) {
                  overlay._timestampMap = {};
                  const overlayTimestamps = oData?.Date;
                  const overlayTimestampsRaw = Array.isArray(oData) ? oData.map(d => d?.timestamp) : null;

                  if (overlayTimestamps && Array.isArray(overlayTimestamps)) {
                    overlayTimestamps.forEach((ts, idx) => {
                      overlay._timestampMap[new Date(ts).getTime()] = idx;
                    });
                  } else if (overlayTimestampsRaw && Array.isArray(overlayTimestampsRaw)) {
                    overlayTimestampsRaw.forEach((ts, idx) => {
                      if (ts) overlay._timestampMap[new Date(ts).getTime()] = idx;
                    });
                  }
                }

                // Get matching index
                const overlayIdx = overlay._timestampMap[barTimestamp] ?? null;

                // Apply to bar.chart[i]
                switch (overlay.type) {
                  case "bb":
                    bar.chart[i] = {
                      bb: {
                        top: overlayIdx != null ? (oData?.BBU?.[overlayIdx] ?? null) : null,
                        middle: overlayIdx != null ? (oData?.BBM?.[overlayIdx] ?? null) : null,
                        bottom: overlayIdx != null ? (oData?.BBL?.[overlayIdx] ?? null) : null,
                      },
                    };
                    break;
                  case "sma":
                    bar.chart[i] = {
                      sma: {
                        sma: overlayIdx != null ? (oData?.SMA?.[overlayIdx] ?? null) : null,
                        close: overlayIdx != null ? (oData?.Close?.[overlayIdx] ?? null) : null,
                        date: overlayIdx != null ? (oData?.Date?.[overlayIdx] ?? null) : null,
                      },
                    };
                    break;
                  case "ema":
                    bar.chart[i] = {
                      ema: {
                        ema: overlayIdx != null ? (oData?.EMA?.[overlayIdx] ?? null) : null,
                        close: overlayIdx != null ? (oData?.Close?.[overlayIdx] ?? null) : null,
                        date: overlayIdx != null ? (oData?.Date?.[overlayIdx] ?? null) : null,
                      },
                    };
                    break;
                  case "psar": {
                    const psarArr = Array.isArray(oData) ? oData : (Array.isArray(oData?.data) ? oData.data : null);
                    bar.chart[i] = {
                      psar: {
                        date: overlayIdx != null && psarArr ? (psarArr[overlayIdx]?.timestamp ?? null) : null,
                        psarl: overlayIdx != null && psarArr ? (psarArr[overlayIdx]?.PSARl ?? null) : null,
                        psars: overlayIdx != null && psarArr ? (psarArr[overlayIdx]?.PSARs ?? null) : null,
                      },
                    };
                    break;
                  }
                }
              });
            });
            console.log('[Char] Overlay data applied directly to historical bars');
          }
        }
      }

      // ===== RE-FETCH INDICATOR DATA FOR EXTENDED DATE RANGE =====
      // If indicators are active, re-fetch them for the full date range so they cover historical data
      if (setting.indicatorParams && setting.indicatorParams.length > 0) {
        const hasActiveIndicators = setting.indicatorParams.some(
          item => item.selectValue && item.selectValue !== '' && item.inputValue && item.inputValue !== ''
        );

        if (hasActiveIndicators) {
          console.log('[Char] Re-fetching indicator data for extended date range...');
          setIsLoadingIndicators(true);

          // Calculate the full date range (oldest bar to now)
          const oldestDate = mergedData[0]?.date;
          const startDateStr = oldestDate ? formatTimestampForApi(new Date(oldestDate).getTime()) : undefined;

          const selectedPeriodForIndicator = periods.find(p =>
            p.period === setting.period ||
            (p.internal === setting.period && p.multiplier === setting.multiplier)
          );
          const indicatorApiPeriod = selectedPeriodForIndicator ? selectedPeriodForIndicator.internal : setting.period || 'minute';
          const indicatorSymbol = tickerFromLocalStorage || setting.symbol;

          // Re-fetch each active indicator with the extended date range
          const indicatorPromises = setting.indicatorParams.map(async (item, index) => {
            if (!item.selectValue || item.selectValue === '' || !item.inputValue || item.inputValue === '') {
              return null;
            }

            try {
              let indicatorData = null;

              switch (item.selectValue) {
                case 'adl': {
                  const adlResp = await getAdlDataNew1(
                    indicatorSymbol, indicatorApiPeriod, setting?.multiplier,
                    startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'adl', data: adlResp?.data || adlResp };
                  break;
                }
                case 'rsi': {
                  const rsiResp = await getRSIDataNew1(
                    indicatorSymbol, indicatorApiPeriod, item.inputValue,
                    setting?.multiplier, startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'rsi', data: rsiResp?.data || rsiResp };
                  break;
                }
                case 'macd': {
                  let fast = 12, slow = 26, signal = 9;
                  if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
                    const parts = item.inputValue.split(',').map(s => s.trim());
                    fast = Number(parts[0]) || 12;
                    slow = Number(parts[1]) || 26;
                    signal = Number(parts[2]) || 9;
                  }
                  const macdResp = await getMACDDataNew1(
                    indicatorSymbol, indicatorApiPeriod, fast, slow, signal,
                    setting?.multiplier, startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'macd', data: macdResp?.data || macdResp };
                  break;
                }
                case 'macdh': {
                  let fast = 12, slow = 26, signal = 9;
                  if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
                    const parts = item.inputValue.split(',').map(s => s.trim());
                    fast = Number(parts[0]) || 12;
                    slow = Number(parts[1]) || 26;
                    signal = Number(parts[2]) || 9;
                  }
                  const macdhResp = await getMACDHDataNew1(
                    indicatorSymbol, indicatorApiPeriod, setting?.multiplier,
                    fast, slow, signal, startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'macdh', data: macdhResp?.data || macdhResp };
                  break;
                }
                case 'stoch': {
                  let fast = 14, slow = 3, smooth_k = 3;
                  if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
                    const parts = item.inputValue.split(',').map(s => s.trim());
                    fast = Number(parts[0]) || 14;
                    slow = Number(parts[1]) || 3;
                    smooth_k = Number(parts[2]) || 3;
                  }
                  const stochResp = await getSTOCHDataNew1(
                    indicatorSymbol, indicatorApiPeriod, setting?.multiplier,
                    fast, slow, smooth_k, startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'stoch', data: stochResp?.data || stochResp };
                  break;
                }
                case 'adx': {
                  let length = 10, scalar = 100, drift = 1, lensig = 14;
                  if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
                    const parts = item.inputValue.split(',').map(s => s.trim());
                    length = Number(parts[0]) || 10;
                    scalar = Number(parts[1]) || 100;
                  }
                  const adxResp = await getADXDataNew1(
                    indicatorSymbol, indicatorApiPeriod, length, scalar, drift, lensig,
                    setting?.multiplier, startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'adx', data: adxResp?.data || adxResp };
                  break;
                }
                case 'cci': {
                  let length = 14, cciScaling = 0.015;
                  if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
                    const parts = item.inputValue.split(',').map(s => s.trim());
                    length = Number(parts[0]) || 14;
                    cciScaling = Number(parts[1]) || 0.015;
                  } else {
                    length = item.inputValue;
                  }
                  const cciResp = await getCCIDataNew1(
                    indicatorSymbol, indicatorApiPeriod, length, cciScaling,
                    setting?.multiplier, startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'cci', data: cciResp?.data || cciResp };
                  break;
                }
                case 'mfi': {
                  let length = 14, drift = 1;
                  if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
                    const parts = item.inputValue.split(',').map(s => s.trim());
                    length = Number(parts[0]) || 14;
                    drift = Number(parts[1]) || 1;
                  } else {
                    length = item.inputValue;
                  }
                  const mfiResp = await getMFIDataNew1(
                    indicatorSymbol, indicatorApiPeriod, length, drift,
                    setting?.multiplier, startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'mfi', data: mfiResp?.data || mfiResp };
                  break;
                }
                case 'obv': {
                  const obvResp = await getOBVDataNew1(
                    indicatorSymbol, indicatorApiPeriod, setting?.multiplier,
                    startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'obv', data: obvResp?.data || obvResp };
                  break;
                }
                case 'ppo': {
                  let fast = 12, slow = 26, signal = 9, scalar = 100;
                  if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
                    const parts = item.inputValue.split(',').map(s => s.trim());
                    fast = Number(parts[0]) || 12;
                    slow = Number(parts[1]) || 26;
                    signal = Number(parts[2]) || 9;
                  }
                  const ppoResp = await getPPODataNew1(
                    indicatorSymbol, indicatorApiPeriod, fast, slow, signal, scalar,
                    setting?.multiplier, startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'ppo', data: ppoResp?.data || ppoResp };
                  break;
                }
                case 'pvo': {
                  let fast = 12, slow = 26, signal = 9, scalar = 100;
                  if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
                    const parts = item.inputValue.split(',').map(s => s.trim());
                    fast = Number(parts[0]) || 12;
                    slow = Number(parts[1]) || 26;
                    signal = Number(parts[2]) || 9;
                  }
                  const pvoResp = await getPVODataNew1(
                    indicatorSymbol, indicatorApiPeriod, fast, slow, signal, scalar,
                    startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'pvo', data: pvoResp?.data || pvoResp };
                  break;
                }
                case 'efi': {
                  let length = 13, drift = 1;
                  if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
                    const parts = item.inputValue.split(',').map(s => s.trim());
                    length = Number(parts[0]) || 13;
                    drift = Number(parts[1]) || 1;
                  } else {
                    length = item.inputValue;
                  }
                  const efiResp = await getEFIDataNew1(
                    indicatorSymbol, indicatorApiPeriod, length, drift,
                    setting?.multiplier, startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'efi', data: efiResp?.data || efiResp };
                  break;
                }
                case 'roc': {
                  let length = 1, scalar = 100;
                  if (typeof item.inputValue === 'string' && item.inputValue.includes(',')) {
                    const parts = item.inputValue.split(',').map(s => s.trim());
                    length = Number(parts[0]) || 1;
                    scalar = Number(parts[1]) || 100;
                  } else {
                    length = item.inputValue;
                  }
                  const rocResp = await getROCDataNew1(
                    indicatorSymbol, indicatorApiPeriod, length, scalar,
                    setting?.multiplier, startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'roc', data: rocResp?.data || rocResp };
                  break;
                }
                case 'cmf': {
                  const cmfResp = await getCMFDataNew1(
                    indicatorSymbol, indicatorApiPeriod, item.inputValue,
                    setting?.multiplier, startDateStr, undefined, overlayRowBudget
                  );
                  indicatorData = { type: 'cmf', data: cmfResp?.data || cmfResp };
                  break;
                }
              }

              if (indicatorData) {
                return { index, data: indicatorData };
              }
            } catch (indicatorErr) {
              console.warn(`[Char] Failed to re-fetch indicator ${item.selectValue}:`, indicatorErr);
            }
            return null;
          });

          // Wait for all indicator fetches
          const indicatorResults = await Promise.allSettled(indicatorPromises);
          const activeIndicators = [];

          indicatorResults.forEach(result => {
            if (result.status === 'fulfilled' && result.value) {
              activeIndicators.push({ index: result.value.index, indicator: result.value.data });
            }
          });

          setIsLoadingIndicators(false);

          if (activeIndicators.length > 0) {
            // Merge indicator data directly into mergedData using timestamp mapping
            // This avoids calling setIndicators(), which would trigger the main useEffect and reset the chart

            // Build timestamp maps for all indicators first
            activeIndicators.forEach(({ indicator }) => {
              if (!indicator || !indicator.data) return;
              const iData = indicator.data;

              if (!indicator._timestampMap) {
                indicator._timestampMap = {};
                if (Array.isArray(iData)) {
                  iData.forEach((item, idx) => {
                    if (item?.timestamp) {
                      // Handle both millisecond timestamps and date strings
                      const ts = typeof item.timestamp === 'number'
                        ? item.timestamp
                        : new Date(item.timestamp).getTime();
                      indicator._timestampMap[ts] = idx;
                    }
                  });
                }
              }
            });

            // Log for debugging
            console.log('[Char] Building indicator timestamp maps');
            if (activeIndicators.length > 0 && activeIndicators[0].indicator?.data?.length > 0) {
              const sampleIndicator = activeIndicators[0].indicator;
              const sampleData = sampleIndicator.data;
              console.log('[Char] Sample indicator timestamps:', {
                first: sampleData[0]?.timestamp,
                last: sampleData[sampleData.length - 1]?.timestamp,
                mapSize: Object.keys(sampleIndicator._timestampMap || {}).length
              });
            }
            if (mergedData.length > 0) {
              console.log('[Char] Sample bar timestamps:', {
                first: new Date(mergedData[0].date).getTime(),
                last: new Date(mergedData[mergedData.length - 1].date).getTime()
              });
            }

            mergedData.forEach((bar) => {
              if (!bar.indicators) bar.indicators = [];
              // Handle both Date objects and timestamps
              const barTimestamp = bar.date instanceof Date
                ? bar.date.getTime()
                : new Date(bar.date).getTime();

              activeIndicators.forEach(({ index: i, indicator }) => {
                if (!indicator || !indicator.type || !indicator.data) return;

                const iData = indicator.data;

                // Get matching index using the timestamp map
                const indicatorIdx = indicator._timestampMap[barTimestamp] ?? null;

                // Apply to bar.indicators[i]
                switch (indicator.type) {
                  case "adl":
                    bar.indicators[i] = {
                      adl: {
                        ad: indicatorIdx != null ? (iData[indicatorIdx]?.ADL ?? iData[indicatorIdx]?.AD ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                  case "rsi":
                    bar.indicators[i] = {
                      rsi: {
                        rsi: indicatorIdx != null ? (iData[indicatorIdx]?.RSI ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                  case "macd":
                    bar.indicators[i] = {
                      macd: {
                        macd: indicatorIdx != null ? (iData[indicatorIdx]?.MACD ?? null) : null,
                        macdh: indicatorIdx != null ? (iData[indicatorIdx]?.MACDh ?? null) : null,
                        macds: indicatorIdx != null ? (iData[indicatorIdx]?.MACDs ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                  case "macdh":
                    bar.indicators[i] = {
                      macdh: {
                        macdh: indicatorIdx != null ? (iData[indicatorIdx]?.MACDh ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                  case "stoch":
                    bar.indicators[i] = {
                      stoch: {
                        stoch: indicatorIdx != null ? (iData[indicatorIdx]?.STOCHk ?? null) : null,
                        stochd: indicatorIdx != null ? (iData[indicatorIdx]?.STOCHd ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                  case "adx":
                    bar.indicators[i] = {
                      adx: {
                        adx: indicatorIdx != null ? (iData[indicatorIdx]?.ADX ?? null) : null,
                        dmn: indicatorIdx != null ? (iData[indicatorIdx]?.DMN ?? null) : null,
                        dmp: indicatorIdx != null ? (iData[indicatorIdx]?.DMP ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                  case "cci":
                    bar.indicators[i] = {
                      cci: {
                        cci: indicatorIdx != null ? (iData[indicatorIdx]?.CCI ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                  case "mfi":
                    bar.indicators[i] = {
                      mfi: {
                        mfi: indicatorIdx != null ? (iData[indicatorIdx]?.MFI ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                  case "obv":
                    bar.indicators[i] = {
                      obv: {
                        obv: indicatorIdx != null ? (iData[indicatorIdx]?.OBV ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                  case "ppo":
                    bar.indicators[i] = {
                      ppo: {
                        ppo: indicatorIdx != null ? (iData[indicatorIdx]?.PPO ?? null) : null,
                        ppoh: indicatorIdx != null ? (iData[indicatorIdx]?.PPOh ?? null) : null,
                        ppos: indicatorIdx != null ? (iData[indicatorIdx]?.PPOs ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                  case "pvo":
                    bar.indicators[i] = {
                      pvo: {
                        pvo: indicatorIdx != null ? (iData[indicatorIdx]?.PVO ?? null) : null,
                        pvoh: indicatorIdx != null ? (iData[indicatorIdx]?.PVOh ?? null) : null,
                        pvos: indicatorIdx != null ? (iData[indicatorIdx]?.PVOs ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                  case "efi":
                    bar.indicators[i] = {
                      efi: {
                        efi: indicatorIdx != null ? (iData[indicatorIdx]?.EFI ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                  case "roc":
                    bar.indicators[i] = {
                      roc: {
                        roc: indicatorIdx != null ? (iData[indicatorIdx]?.ROC ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                  case "cmf":
                    bar.indicators[i] = {
                      cmf: {
                        cmf: indicatorIdx != null ? (iData[indicatorIdx]?.CMF ?? null) : null,
                        date: indicatorIdx != null ? (iData[indicatorIdx]?.timestamp ?? null) : null,
                      },
                    };
                    break;
                }
              });
            });
            console.log('[Char] Indicator data applied directly to historical bars');

            // CRITICAL: Directly update indicatorConfig's .data for each indicator
            // so specialized components get fresh API data.
            // Do NOT use setIndicators() here — that triggers useEffect([indicators])
            // which fully rebuilds indicatorConfig, causing all indicators to remount
            // and reset their viewport.
            setIndicatorConfig((currentConfig) => {
              return currentConfig.map((config) => {
                const match = activeIndicators.find(({ indicator }) =>
                  indicator.type.toUpperCase() === config.id
                );
                if (match && match.indicator.data) {
                  return { ...config, data: match.indicator.data };
                }
                return config;
              });
            });
          }
        }
      }

      setData(mergedData);

      // For META: if we just fetched FB data successfully, keep hasMoreData true to allow more FB fetches
      // Only stop fetching if we received 0 data points AND we already tried FB
      if (formattedPrevious.length === 0) {
        // Only set hasMoreData to false if we already switched to FB and still got no data
        if (!isMetaSymbol(sym) || historicalSymbol === 'FB') {
          setHasMoreData(false);
        }
      }
    } catch (err) {
      console.error('[Char] Error loading more data:', err);
      toast.error('Could not load older chart prices. Try again.');
    } finally {
      setIsLoadingMore(false);
      isFetchingRef.current = false;
    }
  };

  const handleChartPan = (panData) => {
    console.log('[Char] ===== handleChartPan called =====', panData);

    if (!panData) {
      console.log('[Char] ❌ No panData');
      return Promise.resolve();
    }

    if (!hasMoreData) {
      console.log('[Char] ❌ No more data');
      return Promise.resolve();
    }

    if (isLoadingMore) {
      console.log('[Char] ⏳ Already loading');
      return Promise.resolve();
    }

    const reason = panData?.reason;

    if (reason === 'onLoadBefore') {
      console.log('[Char] ✅ ChartCanvas requested older data (onLoadBefore)');
      return loadMoreHistoricalData();
    }

    const { startIndex } = panData;
    const totalBars = data.length;

    console.log('[Char] Data check:', { startIndex, totalBars });

    if (totalBars === 0 || typeof startIndex !== 'number') {
      console.log('[Char] ❌ No data');
      return Promise.resolve();
    }

    // If within 50 bars of left edge, load more
    console.log('[Char] startIndex:', startIndex, '(need <= 50 to trigger)');

    if (startIndex <= 50) {
      console.log('[Char] ✅ TRIGGERING LOAD MORE!');
      return loadMoreHistoricalData();
    }

    return Promise.resolve();
  };
  // ===== END INFINITE SCROLL =====

  // Ref to track last fetched candlestick key to prevent duplicate API calls
  const lastCandlestickFetchKeyRef = useRef(null);

  useEffect(() => {
    const fetchCandlestickData = async () => {
      // Find the selected period object to get the internal value for API
      // Check both period code ("1h") and internal name ("hour") with multiplier
      const selectedPeriodObj = periods.find(p =>
        p.period === setting.period ||
        (p.internal === setting.period && p.multiplier === setting.multiplier)
      );
      const apiPeriod = selectedPeriodObj ? selectedPeriodObj.internal : setting.period || 'minute';

      const sym = tickerFromLocalStorage ? tickerFromLocalStorage : setting.symbol;
      const fetchKey = `${sym}-${apiPeriod}-${setting?.multiplier}`;

      // Skip if same key was already fetched (unless it's a params submit)
      if (lastCandlestickFetchKeyRef.current === fetchKey && !isParamsSubmit) {
        console.log('[char.jsx] Skip duplicate getCandlestickData for:', fetchKey);
        return;
      }
      lastCandlestickFetchKeyRef.current = fetchKey;

      const candlestickParams = {
        symbol: sym,
        startDate: setting.startDate,
        period: apiPeriod,
        multiplier: setting?.multiplier,
      };
      try {
        setloader(true);
        if (setting.indicatorParams.length > 0) {
          setIsIndicatorSubmit(!isIndicatorSubmit);
        }
        if (setting.overlayParams.length > 0) {
          setIsOverlaySubmit(!isOverlaySubmit);
        }

        // For META symbol, only fetch data from 2022-06-01 onwards
        // Earlier data needs to be fetched using FB symbol
        let startDate = isMetaSymbol(sym) ? META_FB_CUTOFF_DATE : candlestickParams.startDate;
        if (isMetaSymbol(sym)) {
          console.log('[Char] META detected - fetching data from', META_FB_CUTOFF_DATE, 'onwards');
        }
        // Per-symbol minimum date (e.g. COIN pre-listing data is junk).
        const symbolMinDate = getSymbolMinDate(sym);
        if (symbolMinDate && (!startDate || new Date(startDate) < new Date(symbolMinDate))) {
          startDate = symbolMinDate;
          console.log(`[Char] ${sym} min-date enforced - fetching from ${symbolMinDate} onwards`);
        }

        const data = await getCandlestickData(
          candlestickParams.symbol,
          startDate,
          candlestickParams.period,
          candlestickParams.multiplier
        );
        setCandleStick(data);
      } catch (error) {
        setSetting(initialValue);
        setCandleStick([]);
        toast.error("Could not load chart prices. Try again.");
        seterror("Something went wrong, please refresh the screen");
        setloader(false);
        console.error(
          "Error fetching Candlestick data:",
          error.response.data.error || error.message
        );
      }
    };
    if (tickerFromLocalStorage || setting?.symbol) {
      fetchCandlestickData();
    }
  }, [isParamsSubmit, tickerFromLocalStorage, setting?.symbol]);

  useEffect(() => {
    fetchSavedCharts();
  }, []);

  const fetchSavedCharts = async () => {
    try {
      const response = await getSavedCharts();
      if (response && response?.data && response?.data?.status === 200) {
        setSavedCharts(response.data.data.data);
      } else {
      }
    } catch (error) {
      console.error("Error fetching saved posts:", error);
    }
  };

  const handleSlideStart = () => {
    setIsSlide(true);
  };

  const handleSlideEnd = () => {
    setIsSlide(false);
  };

  const handleSavedChartClick = (chart) => {
    setShareChartData(chart);
    setChartId(chart?.id);
    setShareChartData1(chart);
    setActiveImage(chart?.image);
    setPreview(true);
  };
  const [showModal, setShowModal] = useState(false);
  const [showModalChart, setShowModalChart] = useState(false);

  const [chartToDelete, setChartToDelete] = useState(null);

  const handleDeleteChart = async () => {
    try {
      const response = await deleteSavedChart(chartToDelete.id);
      if (response?.data?.data) {
        console.log(response);
        toast.success(response.data.message);
        fetchSavedCharts();
      }
    } catch (error) {
      console.error("Error deleting saved chart:", error);
    } finally {
      setShowModal(false);
    }
  };

  const onShareChartDeleteClick = (chart) => {
    setChartToDelete(chart);
    setShowModal(true);
  };

  useEffect(() => {
    try {
      if (Object.keys(candleStick).length > 0 && candleStick.Date && Array.isArray(candleStick.Date) && candleStick.Date.length > 0) {
        const sym = tickerFromLocalStorage || setting.symbol;

        // Align each indicator to the price bars by DATE, not by array position.
        //
        // This block used to read `indicator.data[index]` — row 5 of the
        // indicator glued to bar 5 of the chart, whatever their dates. The two
        // arrays are fetched separately and are routinely different lengths
        // (the service caps rows, and it trims from the OLDEST end, so the
        // shortfall lands on today), which put every value on the wrong bar and
        // left the newest bars null. On intraday timeframes that is the whole
        // visible window, so the panel rendered empty and the line only
        // reappeared if you scrolled far enough left.
        //
        // The re-fetch path below already matches on timestamps; this is the
        // same approach, so both paths now agree instead of one silently
        // disagreeing with the other.
        const indicatorRowByTime = (indicators && Array.isArray(indicators))
          ? indicators.map((indicator) => {
            const map = new Map();
            if (Array.isArray(indicator?.data)) {
              indicator.data.forEach((row) => {
                if (!row || row.timestamp == null) return;
                const ts = typeof row.timestamp === "number"
                  ? row.timestamp
                  : new Date(row.timestamp).getTime();
                if (!Number.isNaN(ts)) map.set(ts, row);
              });
            }
            return map;
          })
          : [];

        let mergedResponse = candleStick.Date.map((date, index) => {
          const mainObject = {
            key: index,
            date: new Date(date),
            open: candleStick.Open && candleStick.Open[index] ? candleStick.Open[index] : 0,
            high: candleStick.High && candleStick.High[index] ? candleStick.High[index] : 0,
            low: candleStick.Low && candleStick.Low[index] ? candleStick.Low[index] : 0,
            close: candleStick.Close && candleStick.Close[index] ? candleStick.Close[index] : 0,
            volume: candleStick.Volume && candleStick.Volume[index] ? candleStick.Volume[index] : 0,
            chart: [],
            indicators: [],
          };

          if (indicators && Array.isArray(indicators)) {
            const barTime = mainObject.date.getTime();
            indicators.map((indicator, i) => {
              // The indicator row recorded for THIS bar's date, or null when the
              // indicator has no value for it. Never a neighbouring row.
              const row = indicatorRowByTime[i]?.get(barTime) ?? null;
              switch (indicator.type) {
                case "adl":
                  mainObject["indicators"][i] = {
                    adl: {
                      ad: row?.AD ?? null,
                      date: row?.timestamp ?? null,
                    },
                  };
                  break;
                case "adx":
                  mainObject["indicators"][i] = {
                    adx: {
                      adx: row?.ADX ?? null,
                      dmn: row?.DMN ?? null,
                      dmp: row?.DMP ?? null,
                      date: row?.timestamp ?? null,
                    },
                  };
                  break;
                case "cci":
                  mainObject["indicators"][i] = {
                    cci: {
                      date: row?.timestamp ?? null,
                      cci: row?.CCI ?? null,
                    },
                  };
                  break;
                case "cmf":
                  mainObject["indicators"][i] = {
                    cmf: {
                      date: row?.timestamp ?? null,
                      cmf: row?.CMF ?? null,
                    },
                  };
                  break;
                case "macd":
                  mainObject["indicators"][i] = {
                    macd: {
                      macd: row?.MACD ?? null,
                      macdh: row?.MACDh ?? null,
                      macds: row?.MACDs ?? null,
                      date: row?.timestamp ?? null,
                    },
                  };
                  break;
                case "macdh":
                  mainObject["indicators"][i] = {
                    macdh: {
                      data: indicator.data,
                      date: row?.timestamp ?? null,
                      macdh: row?.MACDh ?? null,
                    },
                  };
                  break;
                case "mfi":
                  mainObject["indicators"][i] = {
                    mfi: {
                      mfi: row?.MFI ?? null,
                      date: row?.timestamp ?? null,
                    },
                  };
                  break;
                case "obv":
                  mainObject["indicators"][i] = {
                    obv: {
                      date: row?.timestamp ?? null,
                      obv: row?.OBV ?? null,
                    },
                  };
                  break;
                case "ppo":
                  mainObject["indicators"][i] = {
                    ppo: {
                      ppo: row?.PPO ?? null,
                      ppoh: row?.PPOh ?? null,
                      ppos: row?.PPOs ?? null,
                      date: row?.timestamp ?? null,
                    },
                  };
                  break;
                case "pvo":
                  mainObject["indicators"][i] = {
                    pvo: {
                      pvo: row?.PVO ?? null,
                      pvoh: row?.PVOh ?? null,
                      pvos: row?.PVOs ?? null,
                      date: row?.timestamp ?? null,
                    },
                  };
                  break;
                case "roc":
                  mainObject["indicators"][i] = {
                    roc: {
                      roc: row?.ROC ?? null,
                      date: row?.timestamp ?? null,
                    },
                  };
                  break;
                case "efi":
                  mainObject["indicators"][i] = {
                    efi: {
                      efi: row?.EFI ?? null,
                      date: row?.timestamp ?? null,
                    },
                  };
                  break;
                case "stoch":
                  mainObject["indicators"][i] = {
                    stoch: {
                      date: row?.timestamp ?? null,
                      stoch: row?.STOCHk ?? null,
                    },
                  };
                  break;
                case "rsi":
                  mainObject["indicators"][i] = {
                    rsi: {
                      date: row?.timestamp ?? null,
                      rsi: row?.RSI ?? null,
                    },
                  };
                  break;
              }
            });
          }

          if (overlays && Array.isArray(overlays)) {
            overlays.map((overlay, i) => {
              if (!overlay || !overlay.type || !overlay.data) return;

              // Resolve overlay data - handle both formats:
              // 1. Direct data: overlay.data = { Date: [...], BBU: [...], ... }
              // 2. Wrapped (Axios response): overlay.data = { data: { Date: [...], BBU: [...], ... }, status: 200 }
              const oData = overlay.data?.BBU || overlay.data?.SMA || overlay.data?.EMA || overlay.data?.Date
                ? overlay.data  // Direct data format
                : (overlay.data?.data?.BBU || overlay.data?.data?.SMA || overlay.data?.data?.EMA || overlay.data?.data?.Date
                  ? overlay.data.data  // Axios wrapped format
                  : overlay.data);  // Fallback (PSAR record format or unknown)

              // Build timestamp-to-index lookup map for this overlay
              let overlayTimestampMap = null;

              // Get the overlay's timestamp array (varies by type)
              const overlayTimestamps = oData?.Date;
              const overlayTimestampsRaw = Array.isArray(oData) ? oData.map(d => d?.timestamp) : null;

              if (overlayTimestamps && Array.isArray(overlayTimestamps)) {
                // Build map for array-based data (BB, SMA, EMA use Date[] arrays)
                if (!overlay._timestampMap) {
                  overlay._timestampMap = {};
                  overlayTimestamps.forEach((ts, idx) => {
                    const tsKey = new Date(ts).getTime();
                    overlay._timestampMap[tsKey] = idx;
                  });
                }
                overlayTimestampMap = overlay._timestampMap;
              } else if (overlayTimestampsRaw && Array.isArray(overlayTimestampsRaw)) {
                // Build map for record-based data (PSAR uses [{timestamp, PSARl, PSARs}] format)
                if (!overlay._timestampMap) {
                  overlay._timestampMap = {};
                  overlayTimestampsRaw.forEach((ts, idx) => {
                    if (ts) {
                      const tsKey = new Date(ts).getTime();
                      overlay._timestampMap[tsKey] = idx;
                    }
                  });
                }
                overlayTimestampMap = overlay._timestampMap;
              }

              // Get the matching overlay data index for this candlestick bar
              const barTimestamp = new Date(date).getTime();
              const overlayIdx = overlayTimestampMap ? overlayTimestampMap[barTimestamp] : index;

              switch (overlay.type) {
                case "bb":
                  mainObject["chart"][i] = {
                    bb: {
                      top: overlayIdx != null ? (oData?.BBU?.[overlayIdx] ?? null) : null,
                      middle: overlayIdx != null ? (oData?.BBM?.[overlayIdx] ?? null) : null,
                      bottom: overlayIdx != null ? (oData?.BBL?.[overlayIdx] ?? null) : null,
                    },
                  };
                  break;
                case "sma":
                  mainObject["chart"][i] = {
                    sma: {
                      sma: overlayIdx != null ? (oData?.SMA?.[overlayIdx] ?? null) : null,
                      close: overlayIdx != null ? (oData?.Close?.[overlayIdx] ?? null) : null,
                      date: overlayIdx != null ? (oData?.Date?.[overlayIdx] ?? null) : null,
                    },
                  };
                  break;
                case "ema":
                  mainObject["chart"][i] = {
                    ema: {
                      ema: overlayIdx != null ? (oData?.EMA?.[overlayIdx] ?? null) : null,
                      close: overlayIdx != null ? (oData?.Close?.[overlayIdx] ?? null) : null,
                      date: overlayIdx != null ? (oData?.Date?.[overlayIdx] ?? null) : null,
                    },
                  };
                  break;
                case "psar": {
                  // PSAR data can be in record format [{timestamp, PSARl, PSARs}] or unwrapped
                  const psarArr = Array.isArray(oData) ? oData : (Array.isArray(oData?.data) ? oData.data : null);
                  mainObject["chart"][i] = {
                    psar: {
                      date: overlayIdx != null && psarArr ? (psarArr[overlayIdx]?.timestamp ?? null) : null,
                      psarl: overlayIdx != null && psarArr ? (psarArr[overlayIdx]?.PSARl ?? null) : null,
                      psars: overlayIdx != null && psarArr ? (psarArr[overlayIdx]?.PSARs ?? null) : null,
                    },
                  };
                  break;
                }
              }
            });
          }
          return mainObject;
        });

        // For META symbol, filter out any bars before 2022-06-01 or with invalid prices
        // These should be fetched using FB symbol instead
        // Use UTC timestamp AND price check for reliable filtering across all timeframes
        if (isMetaSymbol(sym)) {
          const beforeFilter = mergedResponse.length;
          mergedResponse = mergedResponse.filter(bar => isValidMetaBar(bar));
          const afterFilter = mergedResponse.length;
          if (beforeFilter !== afterFilter) {
            console.log(`[Char] Filtered out ${beforeFilter - afterFilter} invalid META bars (old FB data)`);
          }
        }

        // Defensive per-symbol min-date filter (e.g. COIN pre-listing data is junk).
        if (getSymbolMinDate(sym)) {
          const beforeFilter = mergedResponse.length;
          mergedResponse = mergedResponse.filter(bar => isBarOnOrAfterSymbolMinDate(sym, bar));
          const afterFilter = mergedResponse.length;
          if (beforeFilter !== afterFilter) {
            console.log(`[Char] Filtered out ${beforeFilter - afterFilter} pre-listing bars for ${sym}`);
          }
        }

        setOverlayloader(false);
        // setIndicatorData(false);

        setData(mergedResponse);
        setloader(false);
      }
    } catch (error) {
      seterror(
        error?.message ? "Something went wrong; please refresh the screen" : ""
      );
      setloader(false);
      console.error("Error occured while getting correct formate", error);
    }
  }, [overlays, candleStick, indicators]);

  useEffect(() => {
    saveStateToLocalStorage(setting);
  }, [setting]);

  function scrollToOptions() {
    // Get the element with the specified class name
    const element = document.querySelector(`.chart-options`);

    // Get the element's top position relative to the document
    const elementTop = element.offsetTop;

    // Scroll to the element smoothly
    window.scrollTo({
      top: elementTop,
      behavior: "smooth",
    });
  }

  function openAnnotations() {
    setSetting((prevSetting) => ({
      ...prevSetting,
      ["drawMode"]: !setting.drawMode,
    }));
  }

  const formatMarketCap = (value) => {
    const suffixes = ["", "K", "M", "B", "T"];
    const suffixNum = Math.floor(("" + value).length / 3);
    let shortValue = parseFloat(
      (suffixNum !== 0 ? value / Math.pow(1000, suffixNum) : value).toPrecision(
        2
      )
    );
    if (shortValue % 1 !== 0) {
      shortValue = shortValue.toFixed(2);
    }
    return shortValue + (suffixes[suffixNum] ?? "");
  };
  const isMobileView = document.body.clientWidth <= 768;
  // 1
  const periods = [
    { id: 1, display: "1min", internal: "minute", period: "1m", multiplier: 1 },
    { id: 2, display: "2min", internal: "minute", period: "2m", multiplier: 2 },
    { id: 3, display: "5min", internal: "minute", period: "5m", multiplier: 5 },
    {
      id: 4,
      display: "15min",
      internal: "minute",
      period: "15m",
      multiplier: 15,
    },
    {
      id: 5,
      display: "30min",
      internal: "minute",
      period: "30m",
      multiplier: 30,
    },
    { id: 6, display: "1h", internal: "hour", period: "1h", multiplier: 1 },
    { id: 7, display: "4h", internal: "hour", period: "4h", multiplier: 4 },
    { id: 8, display: "1D", internal: "day", period: "1D", multiplier: 1 },
    { id: 9, display: "1W", internal: "week", period: "1W", multiplier: 1 },
    { id: 10, display: "1M", internal: "month", period: "1M", multiplier: 1 },
  ];
  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    arrow: true,
    slidesToShow: isMobileView ? 1 : 3,
    slidesToScroll: 1,
    prevArrow: !isMobileView && (
      <ArrowButton imgSrc={faChevronLeft} type="left" isMobileView={isWidth} />
    ),
    nextArrow: !isMobileView && (
      <ArrowButton
        imgSrc={faChevronRight}
        type="right"
        isMobileView={isWidth}
      />
    ),
  };

  const [scButton, setScButton] = useState(false);

  //const apiKey = process.env.REACT_APP_API_KEY;
  const apiKey = process.env.REACT_APP_API_KEY;

  const handleStockSelect = async (stock) => {
    if (lastFetchedStockRef.current === stock.symbol) {
      return;
    }
    setloaders(true);
    try {
      const res = await searchStocksApi({ search: stock.symbol, category: stock.category });
      if (res?.data?.data?.length > 0) {
        lastFetchedStockRef.current = stock.symbol;
        currentTickerRef.current = stock.symbol;
        localStorage.setItem("ticker", stock.symbol);
        setTickerFromLocalStorage(stock.symbol);
        const detailRes = await getChartStockDetails({
          ticker: stock.symbol,
          category: stock.category,
        });
        if (detailRes?.data?.data?.ticker?.name) {
          setstockData(detailRes.data.data);
        }
        setSetting((prev) => ({ ...prev, symbol: stock.symbol }));
        window.dispatchEvent(
          new CustomEvent("tickerEvent", { detail: { ticker: stock.symbol } })
        );
      }
    } catch (error) {
      console.error("Error searching stock:", error);
    } finally {
      setloaders(false);
    }
  };

  const handlePeriodChange = (period) => {
    setSetting({
      ...setting,
      period: period.internal,
      multiplier: period.multiplier,
      periodID: period.id,
    });
    setIsParamSubmit(!isParamsSubmit);
  };

  const chartInner = (
    <>
      <ChartTopSummary
        mobileView={isMobileView}
        stockData={stockData}
        tickerFromLocalStorage={tickerFromLocalStorage}
        setting={setting}
        stockPrice={stockPrice}
        indicatorConfig={indicatorConfig}
      />
      <Annotation
        setting={setting}
        setSetting={setSetting}
        drawingCanva={drawingCanva}
      />
      <ChartComp
        initialData={liveData}
        setting={setting}
        indicatorConfig={indicatorConfig}
        drawingCanva={drawingCanva}
        gridSetting={gridSetting}
        setSetting={setSetting}
        setIndicatorConfig={setIndicatorConfig}
        stockData={stockData}
        symbol={
          tickerFromLocalStorage ? tickerFromLocalStorage : setting.symbol
        }
        onLoadMore={handleChartPan}
        isLoadingMore={isLoadingMore}
        setIsLoadingMore={setIsLoadingMore}
        hideZoomControls={hideZoomControls}
      />
    </>
  );

  return (
    <>
      {!(sidebarCollapsedState && !isMobileView) && (
        <DashboardHeader toggle={sidebarToggle} setLoader={setloaders} />
      )}
      {isMobileView && <PageHeaderTitle title={"Chart Search"} />}
      <PageContainer>
        {(isLoadingMore || isLoadingOverlays || isLoadingIndicators) && (
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 9999,
            background: 'rgba(59, 130, 246, 0.9)',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '6px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            fontWeight: 'bold'
          }}>
            {isLoadingIndicators ? '📊 Loading more Indicators data...' : isLoadingOverlays ? '📊 Loading more Overlays data...' : '📊 Loading more historical data...'}
          </div>
        )}
        {/* The desktop layout is NOT gated on the candles. Toolbar, left tool
            strip, bottom timeframe bar and the right rail (watchlist + order
            ticket) render immediately; only the chart band waits. Previously the
            whole page waited on the candles, which is why everything appeared at
            once — after a white gap where the empty ChartError div painted. */}
        {!isMobileView ? (
            <DesktopChartLayout
              chartSlot={
                data.length > 0 ? (
                  <div
                    id="chart-container"
                    ref={captureRef}
                    className="relative w-full h-full"
                    style={{
                      touchAction: "none",
                      width: "100%",
                      margin: 0,
                      padding: 0,
                    }}
                  >
                    {chartInner}
                  </div>
                ) : error ? (
                  <ChartError error={error} />
                ) : (
                  <ChartAreaSkeleton />
                )
              }
              setting={setting}
              setSetting={setSetting}
              periods={periods}
              selectedStock={tickerFromLocalStorage}
              stockData={stockData}
              rows={rows}
              setRows={setRows}
              initialState={initialState}
              isOverlaySubmit={isOverlaySubmit}
              setIsOverlaySubmit={setIsOverlaySubmit}
              isIndicatorSubmit={isIndicatorSubmit}
              setIsIndicatorSubmit={setIsIndicatorSubmit}
              overlayloader={overlayloader}
              indicatorloader={indicatorloader}
              onOpenAnnotations={openAnnotations}
              onAlert={() => setIsNotificationOpen(true)}
              onAddToWatchlist={handleAddToWatchlist}
              onDirectShareChart={directShareChart}
              onStockSelect={handleStockSelect}
              onPeriodChange={handlePeriodChange}
              onBuySell={(type) => {
                localStorage.setItem("buySellType", String(type));
                // CRITICAL: inside a league this must NOT go to /buysell.
                // That route is rendered by ChartSearch, which mounts the order
                // ticket with `game_id={null}` — so an order placed from it is
                // booked to the user's MAIN account, not the league. The league
                // route (/:slug/buysell/:ticker) carries the game_id.
                //
                // The symbol is named in the URL rather than left to
                // localStorage.ticker, so the ticket cannot open on a different
                // instrument from the chart the user was just looking at.
                const sym = currentTickerRef.current || tickerFromLocalStorage;
                if (league?.slug && sym) {
                  navigate(`/${league.slug}/buysell/${encodeURIComponent(sym)}`);
                  return;
                }
                navigate("/buysell");
              }}
              headerHidden={sidebarCollapsedState && !isMobileView}
              editOrder={editOrder}
              onEditOrderDone={clearEditOrder}
            />
          ) : data.length > 0 ? (
            <div className="no-text-select">
              <div id="chart-component" className="m-[-2px] overflow-hidden no-text-select">
                <div
                  className="grow shrink relative"
                  id="chart-container"
                  ref={captureRef}
                  style={{ touchAction: 'none' }}
                >
                  {chartInner}
                </div>
              </div>

              {/* Price Display Div */}
              {/* {currentPrice && (
              <div className="bg-white border border-gray-300 rounded-lg mt-4 px-2 py-1 mr-8" style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="price flex">{currentPrice.toFixed(2)}</div>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  {previousPrice && (
                    <>
                      <div
                        className="diff text-[12px] mr-3"
                        style={{ color: currentPrice >= previousPrice ? 'green' : 'red' }}
                      >
                        {(currentPrice - previousPrice).toFixed(2)}
                      </div>
                      <div
                        className="diff-percentage text-[12px]"
                        style={{ color: currentPrice >= previousPrice ? 'green' : 'red' }}
                      >
                        {previousPrice !== 0 ? ((currentPrice - previousPrice) / previousPrice * 100).toFixed(2) : 0}%
                      </div>
                    </>
                  )}
                </div>
              </div>
            )} */}

              {/* Mobile Chart Toolbar - Shown only on mobile, below the chart */}
              <MobileChartToolbar
                selectedStock={tickerFromLocalStorage}
                setting={setting}
                onStockSelect={async (stock) => {
                  // Skip if same stock already selected
                  if (lastFetchedStockRef.current === stock.symbol) {
                    console.log('[char.jsx] onStockSelect - Skip duplicate:', stock.symbol);
                    return;
                  }

                  setloaders(true);
                  try {
                    const res = await searchStocksApi({ search: stock.symbol, category: stock.category });
                    if (res?.data?.data?.length > 0) {
                      // Update ref BEFORE API call to prevent duplicate call from useEffect
                      lastFetchedStockRef.current = stock.symbol;
                      currentTickerRef.current = stock.symbol;

                      localStorage.setItem("ticker", stock.symbol);
                      setTickerFromLocalStorage(stock.symbol);

                      // Fetch detailed stock data to update the price display
                      const detailRes = await getChartStockDetails({
                        ticker: stock.symbol,
                        category: stock.category,
                      });
                      console.log("MobileChartToolbar - getChartStockDetails response:", detailRes?.data?.data);
                      if (detailRes?.data?.data?.ticker?.name) {
                        setstockData(detailRes.data.data);
                      }

                      // Update setting.symbol to trigger chart data refetch (candlestick)
                      setSetting(prev => ({
                        ...prev,
                        symbol: stock.symbol,
                      }));

                      // Dispatch tickerEvent for other components that might need it
                      // But the stock-details API was already called above, so listeners should skip
                      const myCustomEvent = new CustomEvent("tickerEvent", {
                        detail: { ticker: stock.symbol },
                      });
                      window.dispatchEvent(myCustomEvent);
                    }
                  } catch (error) {
                    console.error("Error searching stock:", error);
                  } finally {
                    setloaders(false);
                  }
                }}
                onPeriodSelect={(period) => {
                  setSetting({
                    ...setting,
                    period: period.internal,
                    multiplier: period.multiplier,
                    periodID: period.id,
                  });
                  setIsParamSubmit(!isParamsSubmit);
                }}
                onPencilClick={openAnnotations}
                onIndicatorsClick={scrollToOptions}
                onMenuClick={scrollToOptions}
                onAlertClick={() => setIsNotificationOpen(true)}
                onOverlayUpdate={() => setIsOverlaySubmit(!isOverlaySubmit)}
                onIndicatorUpdate={() => setIsIndicatorSubmit(!isIndicatorSubmit)}
                onWatchlistClick={handleAddToWatchlist}
                onSaveChartClick={captureElement}
                onDirectShareChart={directShareChart}
                savedCharts={savedCharts}
                onSavedChartClick={handleSavedChartClick}
                onDeleteChart={onShareChartDeleteClick}
                onShareChartAsPost={shareChartAsPost}
                setSetting={setSetting}
                indicatorRows={rows}
                setIndicatorRows={setRows}
                editOrder={editOrder}
                onEditOrderDone={clearEditOrder}
              />
              {!isMobileView && (
                <div>
                  <div
                    className={
                      isMobileView
                        ? "chart-tools mt-8 ml-4"
                        : "chart-tools mt-4 chart-toolbar-offset"
                    }
                  >
                    <ChartToolBar
                      periods={periods}
                      callbackfn={(period) => (
                        <TimeRangeButton
                          key={period?.id}
                          period={period}
                          setting={setting}
                          onClick={() => {
                            setSetting({
                              ...setting,
                              period: period?.period,
                              multiplier: period?.multiplier,
                              periodID: period?.id,
                            });
                            setIsParamSubmit(!isParamsSubmit);
                          }}
                        />
                      )}
                      onClick={openAnnotations}
                      onClick1={scrollToOptions}
                    />
                  </div>

                  <div onMouseDown={(e) => e.preventDefault()}>
                    <StockSummary
                      stockData={stockData}
                      formatMarketCap={formatMarketCap(stockData?.ticker?.mkt_cap)}
                    />
                  </div>

                  <ChartActionButtons
                    onClick={() => setIsNotificationOpen(true)}
                    onClick1={captureElement}
                    onClick2={handleAddToWatchlist}
                  />

                  <div
                    className="justify-center flex-nowrap mt-4 overflow-x-auto grid grid-cols-1 lg:grid-cols-9 chart-options"
                    style={{
                      paddingBottom: isMobileView
                        ? savedCharts.length === 0
                          ? "70px"
                          : "20px"
                        : "40px",
                    }}
                  >
                    <ChartSettingsControl
                      setting={setting}
                      setSetting={setSetting}
                      className="md:flex-shrink-1"
                    />
                    <ChartType
                      setting={setting}
                      setSetting={setSetting}
                      className="md:flex-shrink-1"
                    />
                    <Overlays
                      setting={setting}
                      setSetting={setSetting}
                      isSubmit={isOverlaySubmit}
                      setIsSubmit={setIsOverlaySubmit}
                      loader={overlayloader}
                      className="md:flex-shrink-1"
                    />
                    <Indicators
                      setting={setting}
                      setSetting={setSetting}
                      isSubmit={isIndicatorSubmit}
                      setIsSubmit={setIsIndicatorSubmit}
                      loader={indicatorloader}
                      className="md:flex-shrink-1"
                      setRows={setRows}
                      rows={rows}
                      initialState={initialState}
                    />
                  </div>
                </div>
              )}
            </div>
        ) : error ? (
          <ChartError error={error} />
        ) : (
          /* Mobile, no candles yet: skeleton — NOT ChartError, which rendered an
             empty viewport-tall white block here. */
          <TradeSkeleton mobile />
        )}
        {false && !isMobileView && (
          <SavedCharts
            data={liveData}
            savedCharts={savedCharts}
            mobileView={isMobileView}
            settings={settings}
            onTouchStart={handleSlideStart}
            onTouchEnd={handleSlideEnd}
            callbackfn={(chart, index) => (
              <SaveChartImage
                key={index}
                chart={chart}
                slide={isSlide}
                prop2={() => { }}
                prop3={() => {
                  handleSavedChartClick(chart);
                }}
                type={1}
                toggleDropdown1={toggleDropdown1}
                dropdownStates1={dropdownStates1}
                onShareChartClick={onShareChartClick}
                shareChartAsPost={shareChartAsPost}
                index={index}
                onShareChartDeleteClick={onShareChartDeleteClick}
              />
            )}
            callbackfn1={(chart, index) => (
              <SaveChartImage
                key={index}
                chart={chart}
                slide={isSlide}
                prop2={() => { }}
                prop3={() => {
                  handleSavedChartClick(chart);
                }}
                type={2}
                onShareChartClick={onShareChartClick}
                shareChartAsPost={shareChartAsPost}
                toggleDropdown1={toggleDropdown1}
                dropdownStates1={dropdownStates1}
                index={index}
                onShareChartDeleteClick={onShareChartDeleteClick}
              />
            )}
            preview={preview}
            onClick={() => {
              setPreview(false);
            }}
            src={activeImage}
            onShareChartClick={onShareChartClick}
            shareChartAsPost={shareChartAsPost}
          />
        )}
        {showModal && (
          <div className="fixed z-[100] inset-0 overflow-y-auto">
            <div className="absolute inset-0 bg-black opacity-75"></div>

            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity"
                aria-hidden="true"
              >
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>

              <span
                className="hidden sm:inline-block sm:align-middle sm:h-screen"
                aria-hidden="true"
              >
                &#8203;
              </span>
              <div className="px-2 fixed z-[100] inset-0 overflow-y-auto flex items-center justify-center">
                <div className="bg-white rounded-lg  w-full shadow-xl max-w-lg mx-auto sm:my-8 p-6">
                  {/* Logo */}
                  <div className="flex justify-center items-center mb-4">
                    <img
                      src={"/qushi_logo.png"}
                      alt="Logo"
                      className="h-16 w-auto"
                    />
                  </div>

                  <h2 className="text-lg font-bold text-primaryColor mb-4">
                    Are you sure you want to delete this chart?
                  </h2>

                  <div className="flex items-center justify-between">
                    <button
                      className="bg-primaryColor hover:bg-primaryColor text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                      onClick={handleDeleteChart}
                    >
                      Yes
                    </button>
                    <button
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                      onClick={() => setShowModal(false)}
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {showModalChart && (
          <div className="fixed z-[100] inset-0 overflow-y-auto">
            <div className="absolute inset-0 bg-black opacity-75"></div>

            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity"
                aria-hidden="true"
              >
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>

              <span
                className="hidden sm:inline-block sm:align-middle sm:h-screen"
                aria-hidden="true"
              >
                &#8203;
              </span>
              <div className="px-2 fixed z-[100] inset-0 overflow-y-auto flex items-center justify-center">
                <div className="bg-white rounded-lg w-full shadow-xl max-w-lg mx-auto sm:my-8 p-6">
                  {/* Logo */}
                  <div className="flex justify-center items-center mb-4">
                    <img
                      src={"/qushi_logo.png"}
                      alt="Logo"
                      className="h-16 w-auto"
                    />
                  </div>

                  {/* Composer — same B/I/U + emoji toolbar as Add Post */}
                  <PostRichTextEditor
                    value={inputValue}
                    onChange={setInputValue}
                    placeholder="What's the setup?"
                    isMobileView={isMobileView}
                  />

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-5">
                    <button
                      className="w-full sm:w-auto bg-teal-800 hover:bg-teal-900 text-white font-semibold py-2.5 px-6 rounded-lg disabled:opacity-60 focus:outline-none"
                      onClick={handleShare}
                      disabled={sharingInProgress}
                    >
                      Show Your Work
                    </button>
                    <button
                      className="w-full sm:w-auto border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2.5 px-6 rounded-lg focus:outline-none"
                      onClick={() => setShowModalChart(false)}
                    >
                      Never Mind
                    </button>
                  </div>

                  {/* Lock notice.

                      Client: "League Play Trade Screen share chart icon pops
                      up this modal. Remove the 'your call is on the record'.
                      That line is only for the main site."

                      Same split CreatePostPage already makes between
                      post_lock_notice and post_lock_notice_league — the lock
                      itself still applies inside a league, only the record
                      sentence is a main-site idea. */}
                  <p className="text-xs font-medium text-gray-500 text-center mt-4">
                    {league?.gameId
                      ? "Posts lock after 3 minutes."
                      : "Posts lock after 3 minutes. Your call is on the record."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Direct Share Chart Modal */}
        {showDirectShareModal && (
          <div className="fixed z-[100] inset-0 overflow-y-auto">
            <div className="absolute inset-0 bg-black opacity-75"></div>

            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity"
                aria-hidden="true"
              >
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>

              <span
                className="hidden sm:inline-block sm:align-middle sm:h-screen"
                aria-hidden="true"
              >
                &#8203;
              </span>
              <div className="px-2 fixed z-[100] inset-0 overflow-y-auto flex items-center justify-center">
                <div className="bg-white rounded-lg w-full shadow-xl max-w-lg mx-auto sm:my-8 p-6">
                  {/* Logo */}
                  <div className="flex justify-center items-center mb-4">
                    <img
                      src={"/qushi_logo.png"}
                      alt="Logo"
                      className="h-16 w-auto"
                    />
                  </div>

                  {/* Composer — same B/I/U + emoji toolbar as Add Post */}
                  <PostRichTextEditor
                    value={directShareDescription}
                    onChange={setDirectShareDescription}
                    placeholder="What's the setup?"
                    isMobileView={isMobileView}
                  />

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-5">
                    <button
                      className="w-full sm:w-auto bg-teal-800 hover:bg-teal-900 text-white font-semibold py-2.5 px-6 rounded-lg disabled:opacity-60 focus:outline-none"
                      onClick={handleDirectShare}
                      disabled={directShareInProgress}
                    >
                      Show Your Work
                    </button>
                    <button
                      className="w-full sm:w-auto border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2.5 px-6 rounded-lg focus:outline-none"
                      onClick={() => {
                        setShowDirectShareModal(false);
                        setDirectShareDescription("");
                        setDirectShareImageFile(null);
                      }}
                    >
                      Never Mind
                    </button>
                  </div>

                  {/* Lock notice.

                      Client: "League Play Trade Screen share chart icon pops
                      up this modal. Remove the 'your call is on the record'.
                      That line is only for the main site."

                      Same split CreatePostPage already makes between
                      post_lock_notice and post_lock_notice_league — the lock
                      itself still applies inside a league, only the record
                      sentence is a main-site idea. */}
                  <p className="text-xs font-medium text-gray-500 text-center mt-4">
                    {league?.gameId
                      ? "Posts lock after 3 minutes."
                      : "Posts lock after 3 minutes. Your call is on the record."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {showModal1 && !loading1 && (
          <div
            className={`fixed inset-0 z-[60] flex ${isMobileView ? 'items-end' : 'items-center'} justify-center bg-black/70`}
            onClick={() => setShowModal1(false)}
          >
            <div
              ref={watchlistModalRef}
              className="bg-white rounded-2xl w-full max-w-lg p-4 shadow-2xl border-t border-x border-gray-700 flex flex-col max-h-[80vh]"
              onClick={(e) => e.stopPropagation()}
              style={{
                animation: 'slideUp 0.3s ease-out forwards'
              }}
            >
              {/* Handle bar - draggable to close */}
              {isMobileView && (<div
                className="flex justify-center mb-3 cursor-grab py-2 -mt-2 shrink-0"
                {...createWatchlistSwipeHandlers()}
              >
                <div className="w-10 h-1 bg-gray-600 rounded-full"></div>
              </div>)}

              {/* Header — fixed; only the row list below it scrolls. */}
              <div className="flex justify-between items-center mb-3 shrink-0">
                <h3 className="text-lg font-bold text-[#004d4d]">Save to Watchlist</h3>
                <button
                  onClick={() => setShowModal1(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Rows: plain checkbox + label, no outlined box, label always black
                  — never hover-dependent. (The removed "Select All" row was
                  white-on-white at rest and only legible on hover.) Capped at ~6
                  rows; past that this region scrolls while the title and Done
                  stay put. */}
              <div className="max-h-60 overflow-y-auto -mx-1 px-1 shrink min-h-0">
                {watchlists.length === 0 && (
                  <p className="px-3 py-4 text-sm text-gray-500">
                    No personal watchlists yet
                  </p>
                )}
                {watchlists.map((wl) => {
                  const checked = !!selected1[wl.id];
                  return (
                    <label
                      key={wl.id}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-[#F5F7F7] focus-within:bg-[#F5F7F7]"
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={() => handleCheckboxChange(wl.id)}
                      />
                      <span
                        className={`w-[18px] h-[18px] shrink-0 rounded-[4px] flex items-center justify-center border ${
                          checked
                            ? "bg-[#004c50] border-[#004c50]"
                            : "bg-white border-gray-300"
                        }`}
                      >
                        {checked && (
                          <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <span className="text-sm text-black">{wl.name}</span>
                    </label>
                  );
                })}
              </div>

              <div className="flex justify-center mt-4 shrink-0">
                <button
                  className="w-full px-4 py-3 bg-primaryColor hover:bg-primaryColor-dark text-white font-semibold rounded-lg transition-colors"
                  onClick={handleConfirm}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </PageContainer>

      <AddNotification
        open={isNotificationOpen}
        handleClose={handleClose}
        handleSaveNotification={handleSaveNotification}
        handleAPICall={handleAPICall}
        activeTab={activeTab}
        onClick={() => handleTabClick(1)}
        onClick1={() => handleTabClick(2)}
        value={price}
        onChange={handleInputChange}
      />
      <FollowersListModal
        isOpen={isFollowersOpen}
        // handleShareToFollowers={handleShareToFollowers}
        handleShareCancel={handleShareCancel}
      >
        <div>
          <ul className="px-4 max-h-80 overflow-auto mb-2">
            {followersList && followersList.length > 0 ? (
              followersList.map((val, index) => {
                return (
                  <li key={val?.sender?.id} className="">
                    <div className="flex justify-between py-2 bg-gray-100 px-2 rounded-lg mb-4">
                      <div className="flex px-1">
                        <img
                          alt="profile_image"
                          className="object-cover rounded-full w-10 h-10"
                          src={val?.sender?.image}
                        />
                        <p className="px-1 ml-1 mt-2">
                          {val?.sender?.user_name}
                        </p>
                      </div>
                      <div className="px-1">
                        <button
                          disabled={
                            selectedIndices.includes(index) ? true : false
                          }
                          className={
                            selectedIndices.includes(index)
                              ? "text-primaryColor border border-primaryColor rounded-lg px-2 py-2 text-sm cursor-pointer"
                              : "text-white bg-primaryColor rounded-lg px-2 py-2 text-sm cursor-pointer"
                          }
                          onClick={() =>
                            handleShareToFollowers(val?.sender?.id, index)
                          }
                        >
                          Send
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })
            ) : (
              <div className="text-center font-bold text-black py-4 px-4 mt-3 mb-4">
                <p>No Followers Found</p>
              </div>
            )}
          </ul>
        </div>
      </FollowersListModal>
      {preview1 && (
        <div
          className="flex"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={handlePreview1Close}
        >
          <img
            src={activeImage1}
            alt="Preview"
            style={{ maxWidth: "80%", maxHeight: "90%" }}
          />
        </div>
      )}

      {/* Chart Guide Trigger Button for Desktop*/}
      {!showChartGuide && guideIcon && !isMobileView && !mobileMenuOpen && (
        <button
          className={`fixed text-white p-0.5 rounded-full shadow-lg hover:opacity-90 z-50 select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'
            }`}
          style={{
            bottom: `${guideButtonPosition.bottom}px`,
            right: `${guideButtonPosition.right}px`,
            touchAction: 'none',
            backgroundColor: '#35b23c'
          }}
          onClick={() => !hasDraggedSignificantly && setShowChartGuide(true)}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          title="Drag to move • Click to show guide (Ctrl+G)"
        >
          <svg width="40" height="40" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </button>
      )}

      {/* Chart Guide */}
      {showChartGuide && (
        <ChartGuideSteps setShowGuide={setShowChartGuide} />
      )}

      {/* Mobile Chart Guide Trigger Button */}
      {!showMobileChartGuide && guideIcon && isMobileView && !mobileMenuOpen && (
        <button
          className={`fixed text-white p-0.5 rounded-full shadow-lg hover:opacity-90 z-50 select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'
            }`}
          style={{
            bottom: `${guideButtonPosition.bottom}px`,
            right: `${guideButtonPosition.right}px`,
            touchAction: 'none',
            backgroundColor: '#35b23c'
          }}
          onClick={() => !hasDraggedSignificantly && setShowMobileChartGuide(true)}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          title="Chart Guide"
        >
          <svg width="40" height="40" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </button>
      )}

      {/* Mobile Chart Guide */}
      {showMobileChartGuide && (
        <MobileChartGuideSteps setShowGuide={setShowMobileChartGuide} />
      )}

      {newTraderGuideActive && newTraderGuideStep === 2 && (
        <NewTraderGuide step={2} />
      )}

      {newTraderGuideActive && newTraderGuideStep === 5 &&
        localStorage.getItem("qushi_onboarding_persona") === "experienced" && (
          <NewTraderGuide step={5} />
        )}

      {newTraderGuideActive && newTraderGuideStep === 6 &&
        localStorage.getItem("qushi_onboarding_persona") === "creator" && (
          <NewTraderGuide step={6} />
        )}

      {newTraderGuideActive && newTraderGuideStep === 7 &&
        localStorage.getItem("qushi_onboarding_persona") === "creator" && (
          <NewTraderGuide step={7} />
        )}
    </>
  );
};

const ArrowButton = ({ imgSrc, onClick, type, isMobileView }) => {
  return (
    <button
      onClick={onClick}
      className={
        isMobileView
          ? `absolute top-1/2 z-10 -translate-y-1/2 cursor-pointer ${type === "left"
            ? " left-7 lg:left-[-25px]"
            : "right-4 lg:right-[-25px]"
          } rounded-full p-2 transition duration-150 ease-in-out`
          : `absolute top-1/2 z-10 -translate-y-1/2 cursor-pointer ${type === "left"
            ? "left-5 lg:left-[-25px]"
            : "right-4 lg:right-[-25px]"
          } rounded-full p-2 transition duration-150 ease-in-out`
      }
      style={{
        marginTop: "-12px",
        marginLeft: type === "left" && isMobileView ? "-42px" : "12px",
        marginRight: type === "right" && isMobileView ? "-35px" : "12px",
      }}
    >
      <FontAwesomeIcon className="text-[#5f5f5f]" icon={imgSrc} size="sm" />
    </button>
  );
};

export default Char;
